import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * CrashClawWorkspacePanel — Workspace 工作区配置子模块
 *
 * 功能：
 *   - 拉取用户的 Workspace 列表，卡片网格展示（名称/类型/状态/权限 + 机器IP-环境ID/创建人/目录代码仓 信息网格）
 *   - 一键创建（无 Workspace 时）：自动创建 CrashSight MCP + 默认 Agent + 默认 Workspace
 *   - 手动创建 Workspace Modal（二选一：AnyDev 容器 / CVM 开发机，统一只填名称 + 描述）
 *   - 编辑入口：卡片头铅笔图标 / 卡片底部「更多操作」下拉 / 详情弹窗底部按钮，三处共用同一个编辑 Modal，仅 owner 可见
 *   - 预览 Modal（点击卡片弹出：完整信息展示；底部有编辑/删除按钮）
 *   - 编辑 Modal（可编辑名称 / 描述 / Agent 类型）
 *   - 删除（二次确认）
 *   - 连通性测试（卡片刷新按钮）
 *   - 创建后显示 Bridge 安装命令弹窗
 *   - 知识库（LLM Wiki）：工作区详情弹窗内的旧上传模块已于 2026-08-10 下线（死代码清理，
 *     历史数据不受影响），用户侧知识库能力见配置页同级「知识库」Tab（CrashClawKnowledgePanel.tsx）
 *
 * 后端接口：
 *   - POST /redir/api/crashclaw/listWorkspaces   → Workspace 列表
 *   - POST /redir/api/crashclaw/createWorkspace   → 创建 Workspace
 *   - POST /redir/api/crashclaw/updateWorkspace   → 更新 Workspace
 *   - POST /redir/api/crashclaw/deleteWorkspace   → 删除 Workspace
 *   - POST /redir/api/crashclaw/testWorkspace     → 测试连通性
 *   - POST /redir/api/crashclaw/restartWorkspace  → 重启 Agent（重新加载 MCP）
 *   - POST /redir/api/crashclaw/getWorkspaceLogTail → 读取 Bridge 侧日志尾部（Hermes）
 *   - POST /redir/api/crashclaw/getWorkspaceMcpStatus → 查询 MCP 重启结果
 *   - POST /redir/api/crashclaw/listAgents        → Agent 列表（供关联选择）
 *   - POST /redir/api/crashclaw/listMcpServers    → MCP Server 列表（一键创建用）
 *   - POST /redir/api/crashclaw/createAgent       → 创建 Agent（一键创建用）
 */
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Alert, Button, Dropdown, Form, Input, InputNumber, Menu, message, Modal, Radio, Select, Tag } from 'antd';
import { CloudServerOutlined, CodeOutlined, BookOutlined, CopyOutlined, DeleteOutlined, EditOutlined, EllipsisOutlined, ExclamationCircleOutlined, CheckOutlined, CloseOutlined, FolderOutlined, LoadingOutlined, PlusOutlined, ReloadOutlined, RocketOutlined, ThunderboltOutlined, } from '@ant-design/icons';
import { FiRefreshCw } from 'react-icons/fi';
import { ze } from 'utils/zhEn';
import RestHelper from 'utils/RestHelper';
import { ApiErrorCode } from 'utils/api-error-code';
import { EnvUtil } from 'utils/env-util';
import CsFakePasswordInput from 'components/antd-extension/CsFakePasswordInput';
import { getCrashSightMcpUrl, isCrashSightMcpUrl, } from './useQuickAddCrashSightMcp';
import WorkspaceSharingModal from './WorkspaceSharingModal';
import RepoAuthorizeModal from './RepoAuthorizeModal';
import { buildWorkspaceDisplayName, canManageWorkspace, getWorkspaceDisplayName, isWorkspaceOwner, notifyCrashClawWorkspacesChanged, } from './crashClawWorkspaceUtil';
import { CRASHCLAW_DEFAULT_SYSTEM_PROMPT, CRASHCLAW_MODAL_Z_INDEX, CRASHCLAW_NESTED_MODAL_Z_INDEX, DEFAULT_LLM_MODEL, VENUS_LLM_MODELS } from './crashClawDefaults';
import style from './style.scss';
// ======================== Constants ========================
const API_PREFIX = '/redir/api/crashclaw';
const WORKSPACE_LOG_OPTIONS = [
    { value: 'agent', labelZh: 'Agent 日志', labelEn: 'Agent log', path: '/root/.hermes/logs/agent.log' },
    { value: 'error', labelZh: 'Error 日志', labelEn: 'Error log', path: '/root/.hermes/logs/errors.log' },
    { value: 'gateway', labelZh: 'Gateway 日志', labelEn: 'Gateway log', path: '/root/.hermes/logs/gateway.log' },
    { value: 'bridge', labelZh: 'Bridge 日志', labelEn: 'Bridge log', path: '/root/.crashclaw-bridge/bridge.log' },
];
const DEFAULT_LOG_TAIL_LINES = 200;
/** 日志阅读区固定高度（px），避免切换日志时弹窗抖动 */
const LOG_VIEWER_SCROLL_HEIGHT = 420;
/**
 * Venus 个人 API Key 申请页面 URL。
 * 用户进入此页面可以查看/创建自己的 secretKey，回填到本页面的"LLM API Key"输入框中，
 * 后续会作为 install.sh 的 --llm-api-key 参数（Hermes  LLM Provider 配置必需）。
 */
const VENUS_PERSONAL_ACCOUNT_URL = 'https://venus.woa.com/#/openapi/accountManage/personalAccount';
/** Hermes install.sh 默认 LLM base URL（与 hermes-bridge/install.sh DEFAULT_LLM_BASE_URL 一致） */
const DEFAULT_LLM_BASE_URL = 'http://v2.open.venus.oa.com/llmproxy';
/**
 * CodeBuddy API Key 获取页面 URL。
 * CodeBuddy 使用此 Key 进行认证（通过环境变量 CODEBUDDY_API_KEY），无需交互式登录。
 */
const CODEBUDDY_API_KEY_URL = 'https://codebuddy.woa.com/settings/apikey';
/** 生成不重复名称 */
function generateUniqueName(baseName, existingNames) {
    if (!existingNames.has(baseName))
        return baseName;
    let i = 2;
    while (existingNames.has(`${baseName} (${i})`))
        i++;
    return `${baseName} (${i})`;
}
/**
 * 各 Bridge 形态的 zip 下载地址（COS 静态资源）。
 *
 * 2026-08-07 补充测试服/线上隔离：之前测试服和线上用的是完全同一个固定 URL，
 * 流水线一旦发布新包，测试服和线上会同时、无感知地切到新版本，没法先在测试服
 * 单独验证。现在测试服（`test.crashsight.*`）改用独立的 `crashclaw-cntest/` 目录，
 * 跟线上的 `crashclaw/` 目录互不影响；流水线相应也要把测试服的包传到这个新目录。
 * 这只是最简单能用的隔离方案，真正的版本号管理（按 git 信息生成唯一文件名等）
 * 见另一个单独的需求单，不在这次改动范围内。
 */
const BRIDGE_ZIP_BASE_PATH = EnvUtil.isTestEnv() ? 'crashclaw-cntest' : 'crashclaw';
/**
 * @deprecated 版本化 CDN 上线后（见 20260812_crashclaw_Bridge版本检测与升级提醒_spec.md
 * §3.2/§7.1），这只是 `*.latest.json` 抓取失败时的兜底同名 URL（流水线兼容过渡期仍双写）。
 * 主路径改为 {@link fetchBridgeLatestInfo} + {@link bridgeLatestInfoCache}。
 */
const BRIDGE_ZIP_URLS = {
    hermes: `https://cdn.crashsight.qq.com/${BRIDGE_ZIP_BASE_PATH}/hermes-bridge.zip`,
    codebuddy: `https://cdn.crashsight.qq.com/${BRIDGE_ZIP_BASE_PATH}/codebuddy-bridge.zip`,
};
// ======================== Bridge 版本检测（20260812 spec） ========================
/** capability：支持经 Bridge 转发工蜂 HTTP API（硬门禁，与 bridge_self_upgrade 解耦） */
const CAP_REPO_GONGFENG_API = 'repo_gongfeng_api';
/** capability：支持页面一键升级（识别 bridge_self_upgrade WS 消息） */
const CAP_BRIDGE_SELF_UPGRADE = 'bridge_self_upgrade';
/**
 * capability：支持私有化 SVN 连接校验（repo_svn_test）。
 * 2026-08-13 晚补登记：不要塞进 REQUIRED_BRIDGE_CAPABILITIES——SVN 是可选能力，
 * 缺了不该把整个工作区打成"待升级"硬提醒（Git 仍可用），只在授权弹窗的 SVN 卡片上灰置。
 */
const CAP_REPO_SVN_TEST = 'repo_svn_test';
/**
 * 站点所需 capability（前端硬提醒用，见 spec §7.2 A.2）：与后端门禁常量对齐，
 * P0 至少含已上线依赖 Bridge 的能力。**不要**把 CAP_BRIDGE_SELF_UPGRADE 算进这里——
 * 它只控制一键升级按钮是否可用，不代表"缺了就功能不可用"。
 * **也不要把 CAP_REPO_SVN_TEST 算进这里**（见上方注释）。
 */
const REQUIRED_BRIDGE_CAPABILITIES = [CAP_REPO_GONGFENG_API];
/**
 * `{type}-bridge.latest.json` 抓取结果缓存（模块级，两种 Bridge 类型各一份）。
 *
 * 之所以不用 React state 存储、而用模块级可变对象 + 组件内一个"tick" state 触发重渲染：
 * `generateBridgeInstallCommand` 是纯函数、且在文件多处被同步调用（非 hook 上下文），
 * 改造成 async 会牵动所有调用点。退而求其次：组件挂载时后台拉取一次两种类型的 latest.json
 * 填进这个缓存，取不到时各调用点自动回退到 {@link BRIDGE_ZIP_URLS} 同名 URL。
 */
const bridgeLatestInfoCache = {};
function getBridgeLatestJsonUrl(bridgeType) {
    const fileName = bridgeType === 'codebuddy' ? 'codebuddy-bridge.latest.json' : 'hermes-bridge.latest.json';
    return `https://cdn.crashsight.qq.com/${BRIDGE_ZIP_BASE_PATH}/${fileName}`;
}
async function fetchBridgeLatestInfo(bridgeType) {
    try {
        const resp = await fetch(getBridgeLatestJsonUrl(bridgeType), { cache: 'no-store' });
        if (!resp.ok)
            return null;
        const data = await resp.json();
        if (!data || !data.buildVersion || !data.zipUrl)
            return null;
        const info = { buildVersion: String(data.buildVersion), zipUrl: String(data.zipUrl) };
        bridgeLatestInfoCache[bridgeType] = info;
        return info;
    }
    catch (e) {
        console.warn(`Failed to fetch ${bridgeType}-bridge.latest.json`, e);
        return null;
    }
}
function resolveWorkspaceBridgeType(ws) {
    return (ws.agentType === 'codebuddy' ? 'codebuddy' : 'hermes');
}
function hasBridgeCapability(ws, capability) {
    return !!ws.bridgeCapabilities && ws.bridgeCapabilities.includes(capability);
}
// ======================== 宿主环境能力（hostCapabilities，2026-08-13 新增） ========================
// 跟上面的 bridgeCapabilities（Bridge 程序代码能力，静态、随版本变化）是两个独立概念，
// 见 WorkspaceItem.hostCapabilities 字段注释的完整对比说明。新增"探测宿主机是否装了
// 某个外部命令行工具"这类需求时，常量加在这里，不要加进 CAP_* / bridgeCapabilities 那一组。
/** 宿主环境能力：这台机器（AnyDev 容器/CVM）是否装了 svn 命令行客户端（CrashClaw 代码仓库授权 P1 用） */
const HOST_CAP_SVN_CLI = 'svn_cli';
function hasHostCapability(ws, capability) {
    return !!ws.hostCapabilities && ws.hostCapabilities.includes(capability);
}
/**
 * 明确知道"这台机器没有"某个宿主环境能力（跟 `!hasHostCapability` 不同）——
 * 只有当 Bridge 已经上报过 `hostCapabilities`（数组本身非 null/undefined）、且这个
 * 数组里确实不包含该能力时，才返回 true；如果 `hostCapabilities` 整体为
 * null/undefined（老 Bridge，或者刚连上还没上报过），说明我们根本不知道这台机器
 * 装没装，应该返回 false（不确定 ≠ 确定没有），避免灰置逻辑在这种"未知"场景下
 * 误伤实际已经装好的机器。
 */
function isHostCapabilityKnownMissing(ws, capability) {
    if (!ws || !Array.isArray(ws.hostCapabilities))
        return false;
    return !ws.hostCapabilities.includes(capability);
}
/** 缺少站点所需 capability（如 repo_gongfeng_api）→ 硬提醒，部分功能不可用 */
function isMissingRequiredBridgeCapability(ws) {
    return REQUIRED_BRIDGE_CAPABILITIES.some(cap => !hasBridgeCapability(ws, cap));
}
/** 版本未知（老 Bridge 完全未上报 bridgeBuildVersion） */
function isBridgeVersionUnknown(ws) {
    return !ws.bridgeBuildVersion;
}
/** 能力齐全但落后 CDN 最新构建（软提醒，功能仍可用），字符串全等比较（见 spec §7.3） */
function isBridgeOutdated(ws) {
    if (!ws.bridgeBuildVersion)
        return false;
    const latest = bridgeLatestInfoCache[resolveWorkspaceBridgeType(ws)];
    return !!latest && latest.buildVersion !== ws.bridgeBuildVersion;
}
/** 是否支持一键升级（老包无此 capability 时禁用按钮，引导复制安装命令） */
function canSelfUpgradeBridge(ws) {
    return hasBridgeCapability(ws, CAP_BRIDGE_SELF_UPGRADE);
}
/** 卡片轻量 Tag 判定：缺能力/版本未知 = "待升级"（硬）；仅落后 latest = "可升级"（软） */
function getWorkspaceUpgradeTagKind(ws) {
    if (isBridgeVersionUnknown(ws) || isMissingRequiredBridgeCapability(ws))
        return 'hard';
    if (isBridgeOutdated(ws))
        return 'soft';
    return null;
}
/** 页面默认展示的 Bridge 类型（优先 CodeBuddy） */
const DEFAULT_BRIDGE_TYPE = 'codebuddy';
/** AnyDev 一键创建支持两种 Bridge，由用户在工作区创建时选择 */
/** CVM 安装命令 / 工作区卡片支持展示两种 Bridge 形态 */
const SHOW_CODEBUDDY_BRIDGE_OPTION = true;
function getEffectiveBridgeType(type) {
    return SHOW_CODEBUDDY_BRIDGE_OPTION ? type : DEFAULT_BRIDGE_TYPE;
}
/**
 * 生成 Bridge 安装命令。
 *
 * 流程：下载 zip → 解压 → 执行 install.sh → 启动 Bridge。
 * install.sh 会自动完成：
 *   - Hermes ：安装 Hermes Agent（若未装）+ 自动写入 LLM Provider 配置（使用默认 Venus base URL
 *     + 默认模型 claude-sonnet-4-6 + 用户传入的 API Key）+ 写入 API_SERVER_KEY 到 Hermes .env 和 Bridge config.yaml
 *     + 启动 Hermes gateway + 启动 Bridge
 *   - CodeBuddy ：安装 CodeBuddy CLI（若未装）+ 创建 Python venv + 生成 Bridge config.yaml（默认模型 claude-sonnet-4-6）+ 启动脚本
 *
 * --server 参数取当前页面域名，install.sh 据此拼出 WS endpoint（wss://<domain>/ws/bridge）。
 * --llm-api-key 参数：用户从 Venus 个人账号页面拿到的 secretKey，install.sh 会写入 Hermes 配置中。
 *                    Hermes 必填（不填脚本会在 Step 4 报错退出）。CodeBuddy 当前无 LLM 配置，
 *                    传了也会被 install.sh 忽略，所以这里只在 hermes 时拼进去。
 * --codebuddy-api-key 参数：用户从 https://codebuddy.woa.com/settings/apikey 获取的 API Key，
 *                    install.sh 会将其写入环境变量 CODEBUDDY_API_KEY + CODEBUDDY_INTERNET_ENVIRONMENT=internal，
 *                    使 CodeBuddy CLI 无需交互式登录即可使用。仅在 codebuddy 时拼进去。
 */
export function generateBridgeInstallCommand(bridgeToken, bridgeType = DEFAULT_BRIDGE_TYPE, llmApiKey, llmBaseUrl) {
    const server = window.location.hostname;
    // 优先用 latest.json 缓存的版本化 zipUrl（见 §7.1）；未取到（尚在拉取/网络失败）时
    // 回退旧同名 zip URL（流水线兼容过渡期仍双写，见 spec §3.5）。
    const zipUrl = bridgeLatestInfoCache[bridgeType]?.zipUrl || BRIDGE_ZIP_URLS[bridgeType];
    const tmpDir = bridgeType === 'hermes' ? '/tmp/crashclaw-hermes-bridge' : '/tmp/crashclaw-codebuddy-bridge';
    const tmpZip = `${tmpDir}.zip`;
    // ⚠️ 重要：bash install.sh 这一行必须是粘贴命令的最后一行（后面不能再跟任何命令 / 注释 / 空行）。
    //
    // 原因：install.sh 的 Step 4 历史上会交互式 read URL / API Key / Model。当用户把整段命令粘贴到终端后，
    // bash 按行执行到 `bash install.sh` 时，**终端 stdin 缓冲区里还有粘贴的后续行**。install.sh 的
    // `read -p` 会直接把那些后续行吞成用户输入，导致 URL / key 被填成 `rm -rf /tmp/xxx` 这种错乱内容。
    //
    // 现在 install.sh 通过 `--llm-api-key` 等参数提前注入 LLM 配置，Step 4 已经完全非交互化，
    // 但保险起见仍然保持"最后一行就是 bash install.sh"的约束（多参数用反斜杠续行，仍属单一逻辑命令）。
    const trimmedKey = (llmApiKey || '').trim();
    const trimmedBaseUrl = (llmBaseUrl || '').trim();
    // 用单引号包裹 key，避免 shell 对 ! / $ / 空格等字符做转义解析（Venus key 形如 xxx@1234 含特殊字符）
    const keyArg = trimmedKey && bridgeType === 'hermes' ? ` --llm-api-key '${trimmedKey}'` : '';
    const baseUrlArg = trimmedBaseUrl && bridgeType === 'hermes' ? ` --llm-base-url '${trimmedBaseUrl}'` : '';
    // CodeBuddy ：API Key 用于初始化 CODEBUDDY_API_KEY 环境变量，无需交互式登录
    const codebuddyKeyArg = trimmedKey && bridgeType === 'codebuddy' ? ` --codebuddy-api-key '${trimmedKey}'` : '';
    return `curl -fsSL -o ${tmpZip} ${zipUrl}
unzip -qo ${tmpZip} -d ${tmpDir}
bash ${tmpDir}/install.sh --server ${server} --bridge-key "${bridgeToken}"${keyArg}${baseUrlArg}${codebuddyKeyArg}`;
}
const CrashClawWorkspacePanel = ({ initialWorkspaceUid }) => {
    const [workspaces, setWorkspaces] = useState([]);
    const [agents, setAgents] = useState([]);
    const [mcpServers, setMcpServers] = useState([]);
    const [loading, setLoading] = useState(false);
    // Modal 状态
    const [modalVisible, setModalVisible] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selectedWorkspace, setSelectedWorkspace] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    /** 详情弹窗顶部 / 基本信息名称行内编辑 */
    const [inlineNameEditing, setInlineNameEditing] = useState(false);
    const [inlineNameDraft, setInlineNameDraft] = useState('');
    const [inlineNameSaving, setInlineNameSaving] = useState(false);
    // 代码仓库授权（工蜂 Git，P0）：预览弹窗打开的工作区对应的已授权仓库扁平列表
    const [repoBindings, setRepoBindings] = useState([]);
    const [repoBindingsLoading, setRepoBindingsLoading] = useState(false);
    // 2026-08-09 review 修复：查询失败时跟"真的没有绑定仓库"要展示不同的状态，
    // 否则用户分不清是接口挂了还是自己确实没配过仓库
    const [repoBindingsError, setRepoBindingsError] = useState(false);
    const [repoAuthorizeModalVisible, setRepoAuthorizeModalVisible] = useState(false);
    // 2026-08-14：该工作区已存在的 SVN 凭证（credentialUid + svnUrl + username），供
    // ① RepoAuthorizeModal 的"追加路径"模式预填复用；② SVN 组标题右侧"修改凭证"Modal 展示/编辑。
    // 单组限制下至多一条；没有时为 null。
    const [svnCredential, setSvnCredential] = useState(null);
    // 2026-08-14：SVN 凭证编辑 Modal（改密码 / 删整组）
    const [svnCredentialEditVisible, setSvnCredentialEditVisible] = useState(false);
    const [editSvnPassword, setEditSvnPassword] = useState('');
    const [savingSvnCredential, setSavingSvnCredential] = useState(false);
    const [deletingSvnCredential, setDeletingSvnCredential] = useState(false);
    // 2026-08-09 review 修复：记录"当前这次 loadRepoBindings 是为哪个工作区发起的"，
    // 响应回来时如果用户已经切换到别的工作区（这个 ref 被后面的调用改写了），就丢弃这次
    // 响应，避免旧请求的响应在新请求之后才回来，把当前展示的工作区的仓库列表覆盖成另一个
    // 工作区的数据（listRepoCredentials 本身没有做请求取消/排序保护）
    const repoBindingsRequestWorkspaceUidRef = useRef('');
    // 一键创建状态
    const [quickCreating, setQuickCreating] = useState(false);
    const [quickCreateStep, setQuickCreateStep] = useState('');
    // 创建表单选中的环境类型（AnyDev / CVM 开发机）
    const [createEnvType, setCreateEnvType] = useState('anydev');
    // AnyDev 创建链路：CrashSight MCP 自动配置 + 默认 Agent 自动创建 + 弹共享范围
    const [anydevCreating, setAnydevCreating] = useState(false);
    const [anydevCreateStep, setAnydevCreateStep] = useState('');
    // 共享范围弹窗（AnyDev 创建成功后自动弹）
    const [sharingModalVisible, setSharingModalVisible] = useState(false);
    const [sharingTarget, setSharingTarget] = useState(null);
    /**
     * 每个工作区的共享范围摘要：'self' | 'team'。
     *   - 'team' = 有共享给其他人（managers+users 去重后 >1 个 rtx，或者除 creator 外仍有成员）
     *   - 'self' = 仅自己
     * 卡片上根据此值显示 tag；保存后刷新对应条目。
     */
    const [sharingScopeMap, setSharingScopeMap] = useState({});
    // Bridge 安装命令弹窗
    // modalBridgeType / modalBridgeToken：modal 场景下记录当前选中的 Bridge 类型和 token
    // modalWorkspaceUid：AnyDev 一键部署按钮需要这个值（确定要对哪个 Workspace 创建容器）
    // 和下面卡片内的 cardBridgeTypes 分开管理，互不干扰
    const [bridgeCmdVisible, setBridgeCmdVisible] = useState(false);
    const [modalBridgeToken, setModalBridgeToken] = useState('');
    const [modalBridgeType, setModalBridgeType] = useState(DEFAULT_BRIDGE_TYPE);
    const [modalWorkspaceUid, setModalWorkspaceUid] = useState('');
    // 卡片上每个 Workspace 可能选中不同的 Bridge 类型（用户展开后切换过），按 workspaceUid 存
    const [cardBridgeTypes, setCardBridgeTypes] = useState({});
    // LLM API Key（Venus secretKey）。Hermes 必须配置，否则 install.sh / AnyDev 启动后会卡在 Step 4。
    //
    // 两套独立 state：
    //   - modalLlmApiKey：Bridge 命令 Modal（Workspace 刚创建完弹出的那个）使用
    //   - cardLlmApiKeys：Workspace 卡片展开"安装命令"区域使用，按 workspaceUid 隔离
    // 不持久化到后端：避免把用户的 Venus key 落库，仅在前端会话内驻留，刷新页面就清空（用户需重新填）。
    const [modalLlmApiKey, setModalLlmApiKey] = useState('');
    const [cardLlmApiKeys, setCardLlmApiKeys] = useState({});
    function getCardLlmApiKey(workspaceUid) {
        return cardLlmApiKeys[workspaceUid] || '';
    }
    function setCardLlmApiKey(workspaceUid, key) {
        setCardLlmApiKeys(prev => ({ ...prev, [workspaceUid]: key }));
    }
    function getCardBridgeType(workspaceUid) {
        return cardBridgeTypes[workspaceUid] || DEFAULT_BRIDGE_TYPE;
    }
    function setCardBridgeType(workspaceUid, type) {
        // 切换 Bridge 类型时清空对应 workspace 的 key 输入框。
        // 因为 Hermes 用的是 Venus secretKey，CodeBuddy 用的是 CodeBuddy API Key，
        // 同一个 state 字段如果不清空，用户切换后会把上一个的 key 误传给当前的 install.sh。
        setCardBridgeTypes(prev => ({ ...prev, [workspaceUid]: type }));
        setCardLlmApiKeys(prev => ({ ...prev, [workspaceUid]: '' }));
    }
    // 连通性测试状态（卡片层的刷新按钮仍保留）
    const [testingWorkspaceIds, setTestingWorkspaceIds] = useState(new Set());
    // 手动重启 Agent（重新加载 MCP 配置；Hermes / CodeBuddy 等均适用）
    const [restartingWorkspaceIds, setRestartingWorkspaceIds] = useState(new Set());
    // 一键升级 Bridge（见 20260812_crashclaw_Bridge版本检测与升级提醒_spec.md §5.6/§7.3）
    const [upgradingWorkspaceIds, setUpgradingWorkspaceIds] = useState(new Set());
    // bridgeLatestInfoCache 是模块级可变对象，这个 tick 仅用于在拉取完成后触发一次重渲染，
    // 让读取该缓存的渲染逻辑（安装命令 / Alert / Tag）能拿到最新值
    const [, setBridgeLatestInfoTick] = useState(0);
    const [llmModels, setLlmModels] = useState(VENUS_LLM_MODELS);
    const [modelUpdatingWorkspaceUid, setModelUpdatingWorkspaceUid] = useState(null);
    /** 共享工作区管理者看不到所有者 Agent 列表，用此缓存 getWorkspaceModel / 本地切换结果 */
    const [workspaceModelMap, setWorkspaceModelMap] = useState({});
    // Hermes 工作区日志查看
    const [logModalVisible, setLogModalVisible] = useState(false);
    const [logModalWorkspace, setLogModalWorkspace] = useState(null);
    const [logModalKey, setLogModalKey] = useState('agent');
    const [logModalLines, setLogModalLines] = useState(DEFAULT_LOG_TAIL_LINES);
    const [logModalLoading, setLogModalLoading] = useState(false);
    const [logModalContent, setLogModalContent] = useState('');
    const [logModalMeta, setLogModalMeta] = useState(null);
    const logScrollRef = useRef(null);
    const logDisplayLines = useMemo(() => (logModalContent ? logModalContent.split('\n') : []), [logModalContent]);
    const [form] = Form.useForm();
    // -------------------- Data fetching --------------------
    const fetchData = useCallback(async (silent = false) => {
        if (!silent)
            setLoading(true);
        try {
            const [wsRsp, agentRsp, mcpRsp] = await Promise.all([
                RestHelper.mustPost(`${API_PREFIX}/listWorkspaces`),
                RestHelper.mustPost(`${API_PREFIX}/listAgents`),
                RestHelper.mustPost(`${API_PREFIX}/listMcpServers`),
            ]);
            const nextWorkspaces = wsRsp.json.data || [];
            setWorkspaces(nextWorkspaces);
            setAgents(agentRsp.json.data || []);
            setMcpServers(mcpRsp.json.data || []);
            return nextWorkspaces;
        }
        catch (e) {
            console.error('Failed to fetch workspace data', e);
            return [];
        }
        finally {
            if (!silent)
                setLoading(false);
        }
    }, []);
    const fetchLlmModels = useCallback(async () => {
        try {
            const rsp = await RestHelper.mustPost(`${API_PREFIX}/listLlmModels`, {});
            const data = rsp.json?.data;
            if (data?.length) {
                setLlmModels(data);
            }
            // 后端无返回时保留前端白名单兜底
        }
        catch (e) {
            console.error('Failed to fetch LLM models, fallback to static whitelist', e);
        }
    }, []);
    // 拉取两种 Bridge 类型的 latest.json（版本化 CDN 指针），供安装命令 / 版本 Alert /
    // 卡片 Tag 使用（见 spec §7.1）。CDN 静态资源，用原生 fetch 而非 RestHelper（非后端 API）。
    useEffect(() => {
        Promise.all([fetchBridgeLatestInfo('hermes'), fetchBridgeLatestInfo('codebuddy')])
            .then(() => setBridgeLatestInfoTick(t => t + 1))
            .catch(() => { });
    }, []);
    useEffect(() => {
        fetchData();
        fetchLlmModels();
        // 每 30 秒主动探测每个 Workspace 的 Bridge 在线状态，并刷新列表元数据。
        //
        // ⚠️ 仅靠 listWorkspaces 不够：后端 healthStatus 只在 Bridge WebSocket 连接事件
        // （建立/断开）时写入 DB。如果 Bridge 异常掉线（网络分区、Pod 漂移、进程崩溃没触发
        // close 事件等），DB 里会一直停留在旧的 HEALTHY，前端拉再多次都是同一个旧值。
        // 必须主动调 testWorkspace 让后端实时探测一次，才能拿到真实状态。
        //
        // 页面在后台（切到其他 Tab / 最小化）时跳过轮询，避免无效请求浪费服务端资源。
        const pollTimer = setInterval(() => {
            if (document.visibilityState !== 'visible')
                return;
            // 1) 先刷新列表元数据（名称/描述/agentName 等可能被其他端改过）
            fetchData(true);
            // 2) 静默探测每个 Workspace 的 Bridge 状态。silent=true 不显示卡片刷新转圈动画，
            //    避免 UI 每 30s 闪一下。失败 testWorkspaceByUid 内部已 catch 不弹 toast。
            //    通过 setWorkspaces 回调拿最新列表，避免闭包陈旧问题。
            setWorkspaces(currentWorkspaces => {
                currentWorkspaces.forEach(ws => testWorkspaceByUid(ws.workspaceUid, true));
                return currentWorkspaces; // 不修改 state
            });
        }, 30_000);
        return () => clearInterval(pollTimer);
    }, [fetchData]);
    function getAgentModelPreference(agentUid, workspaceUid) {
        if (workspaceUid && workspaceModelMap[workspaceUid]) {
            return workspaceModelMap[workspaceUid];
        }
        if (!agentUid)
            return DEFAULT_LLM_MODEL;
        const agent = agents.find(a => a.agentUid === agentUid);
        return agent?.modelPreference || DEFAULT_LLM_MODEL;
    }
    async function handleWorkspaceModelChange(workspaceUid, _agentUid, model) {
        if (modelUpdatingWorkspaceUid)
            return;
        setModelUpdatingWorkspaceUid(workspaceUid);
        try {
            // 走工作区级接口，允许 sharing.managers 修改（updateAgent 仅所有者可改）
            await RestHelper.mustPost(`${API_PREFIX}/updateWorkspaceModel`, {
                workspaceUid,
                model,
            });
            setWorkspaceModelMap(prev => ({ ...prev, [workspaceUid]: model }));
            message.success(ze('模型已更新，Agent 将自动重启以生效', 'Model updated; Agent will restart to apply'));
            await fetchData(true);
        }
        catch (e) {
            console.error('Failed to update workspace model', e);
            message.error(ze('模型更新失败，请稍后重试', 'Failed to update model, please retry'));
        }
        finally {
            setModelUpdatingWorkspaceUid(null);
        }
    }
    // -------------------- Helpers --------------------
    /** 获取健康状态展示样式 class */
    function getHealthDotClass(status) {
        if (status === 'HEALTHY')
            return style.skillStatusDotEnabled;
        if (status === 'UNHEALTHY')
            return style.workspaceStatusDotUnhealthy;
        return style.skillStatusDotDisabled;
    }
    function getHealthText(status) {
        if (status === 'HEALTHY')
            return ze('在线', 'Online');
        if (status === 'UNHEALTHY')
            return ze('离线', 'Offline');
        return ze('未知', 'Unknown');
    }
    function getHealthTextClass(status) {
        if (status === 'HEALTHY')
            return style.skillStatusTextEnabled;
        if (status === 'UNHEALTHY')
            return style.workspaceStatusTextUnhealthy;
        return style.skillStatusTextDisabled;
    }
    // -------------------- 共享范围（卡片层 + 编辑入口） --------------------
    /**
     * 拉单个工作区的共享范围摘要：managers + users 去重后 >1 个 rtx 视为"团队可见"。
     * mock 后端没保存过的 ws 返回空数组，会被记为 'self'。
     */
    async function fetchWorkspaceSharingScope(workspaceUid) {
        try {
            const rsp = await RestHelper.mustPost(`${API_PREFIX}/getWorkspaceSharing?workspaceUid=${encodeURIComponent(workspaceUid)}`);
            const d = rsp.json.data || { managers: [], users: [] };
            const all = new Set([...(d.managers || []), ...(d.users || [])]);
            // 创建人也算 owner 自带 1 个，>=2 才算共享出去
            return all.size > 1 ? 'team' : 'self';
        }
        catch (e) {
            console.error('fetchWorkspaceSharingScope failed', e);
            return 'self';
        }
    }
    /** 列表数据变化后，并发拉所有 ws 的共享范围摘要，刷新到 sharingScopeMap */
    useEffect(() => {
        if (workspaces.length === 0) {
            setSharingScopeMap({});
            return;
        }
        let cancelled = false;
        (async () => {
            const ownedWorkspaces = workspaces.filter(ws => isWorkspaceOwner(ws));
            const entries = await Promise.all(ownedWorkspaces.map(async (ws) => [ws.workspaceUid, await fetchWorkspaceSharingScope(ws.workspaceUid)]));
            if (cancelled)
                return;
            setSharingScopeMap(Object.fromEntries(entries));
        })();
        return () => { cancelled = true; };
        // 故意只依赖 workspaceUid 列表，避免名称/描述变化触发无谓的重拉
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workspaces.map(w => w.workspaceUid).join(',')]);
    function openSharingModalForWorkspace(ws) {
        setSharingTarget({
            uid: ws.workspaceUid,
            name: getWorkspaceDisplayName(ws),
            creatorRtx: ws.ownerRtx || undefined,
        });
        setSharingModalVisible(true);
    }
    // -------------------- 连通性测试（卡片层） --------------------
    async function testWorkspaceByUid(workspaceUid, silent = false) {
        if (!silent) {
            setTestingWorkspaceIds(prev => new Set(prev).add(workspaceUid));
        }
        try {
            const rsp = await RestHelper.mustPost(`${API_PREFIX}/testWorkspace`, { workspaceUid });
            const result = rsp.json.data;
            // 本地即时更新健康状态，避免重新 fetch 列表
            setWorkspaces(prev => prev.map(ws => {
                if (ws.workspaceUid === workspaceUid) {
                    return {
                        ...ws,
                        healthStatus: result.success ? 'HEALTHY' : 'UNHEALTHY',
                        lastHealthCheckTime: new Date().toISOString(),
                    };
                }
                return ws;
            }));
        }
        catch (e) {
            console.error('Test workspace failed', e);
        }
        finally {
            if (!silent) {
                setTestingWorkspaceIds(prev => {
                    const next = new Set(prev);
                    next.delete(workspaceUid);
                    return next;
                });
            }
        }
    }
    async function pollMcpRestartResult(workspaceUid, maxAttempts = 15) {
        for (let i = 0; i < maxAttempts; i += 1) {
            await new Promise(resolve => setTimeout(resolve, 2000));
            try {
                const rsp = await RestHelper.mustPost(`${API_PREFIX}/getWorkspaceMcpStatus`, {
                    workspaceUid,
                });
                const d = rsp.json.data;
                if (!d?.hasEvent)
                    continue;
                if (d.triggeredBy !== 'manual')
                    continue;
                if (typeof d.ageMs === 'number' && d.ageMs > 120 * 1000)
                    continue;
                if (d.eventType === 'applied' || d.eventType === 'failed') {
                    return d;
                }
            }
            catch (e) {
                console.warn('pollMcpRestartResult failed', e);
            }
        }
        return null;
    }
    async function runRestartInBackground(workspaceUid) {
        setRestartingWorkspaceIds(prev => new Set(prev).add(workspaceUid));
        const messageKey = `restart-${workspaceUid}`;
        try {
            const rsp = await RestHelper.mustPost(`${API_PREFIX}/restartWorkspace`, {
                workspaceUid,
            });
            const hint = rsp.json.data?.message;
            message.loading({
                content: hint || ze('Agent 正在后台重启，可继续浏览其他页面…', 'Agent is restarting in the background. You can navigate freely.'),
                key: messageKey,
                duration: 0,
            });
            const result = await pollMcpRestartResult(workspaceUid);
            message.destroy(messageKey);
            if (result?.eventType === 'applied') {
                message.success(ze(`Agent 已重启，MCP 配置已生效（耗时 ${result.durationSec ?? '?'}s）`, `Agent restarted; MCP config applied (${result.durationSec ?? '?'}s)`));
            }
            else if (result?.eventType === 'failed') {
                message.error({
                    content: ze(`重启失败：${result.error || '未知错误'}。请在开发机上手动重启 Agent 后重试。`, `Restart failed: ${result.error || 'unknown error'}. Please restart the Agent manually on your dev machine and try again.`),
                    duration: 10,
                });
            }
            else {
                message.info(ze('重启指令已发送，MCP 配置将在数秒内生效。若长时间未生效，请刷新页面后重试。', 'Restart command sent. MCP config should apply within seconds. If it does not, refresh and try again.'));
            }
        }
        catch (e) {
            message.destroy(messageKey);
            console.error('Restart workspace failed', e);
        }
        finally {
            setRestartingWorkspaceIds(prev => {
                const next = new Set(prev);
                next.delete(workspaceUid);
                return next;
            });
        }
    }
    function restartWorkspaceByUid(workspaceUid) {
        const ws = workspaces.find(w => w.workspaceUid === workspaceUid);
        if (!ws)
            return;
        if (ws.healthStatus !== 'HEALTHY') {
            message.warning(ze('工作区离线，无法重启。请确保 Bridge 在线后再试。', 'Workspace is offline. Please ensure Bridge is online before restarting.'));
            return;
        }
        Modal.confirm({
            title: ze('重启 Agent', 'Restart Agent'),
            icon: _jsx(ReloadOutlined, { style: { color: '#1890ff' } }),
            content: (_jsxs("div", { children: [_jsx("p", { children: ze('将重新加载最新 MCP / Skills 配置并重启 Agent，过程约 5-15 秒。进行中的聊天可能短暂中断。', 'This will reload the latest MCP / Skills config and restart the Agent (about 5-15 seconds). Active chats may be briefly interrupted.') }), _jsx("p", { children: ze('确认继续？', 'Continue?') })] })),
            okText: ze('确认重启', 'Restart'),
            cancelText: ze('取消', 'Cancel'),
            onOk: () => {
                void runRestartInBackground(workspaceUid);
            },
        });
    }
    // -------------------- 一键升级 Bridge（与"重启 Agent"语义不同：更新 Bridge 程序本身） --------------------
    /**
     * fetchData 刷新的是 workspaces 列表状态；若详情弹窗当前正打开着这个工作区，
     * 还要同步一份到 selectedWorkspace（复用 saveInlineName 已验证过的双写模式），
     * 否则弹窗会一直渲染打开时刻的旧快照——版本号、Alert、按钮状态都不会跟着刷新
     * （2026-08-12 review 修复：升级成功后 toast 已提示最新，详情弹窗却仍显示旧版本/
     * 可点击的「升级 Bridge」）。
     */
    function syncSelectedWorkspaceFrom(latestList, workspaceUid) {
        const latestWs = latestList.find(w => w.workspaceUid === workspaceUid);
        if (!latestWs)
            return;
        setSelectedWorkspace(prev => (prev && prev.workspaceUid === workspaceUid ? latestWs : prev));
    }
    /**
     * 升级后轮询 listWorkspaces，直到该工作区 bridgeBuildVersion 变为期望的目标版本
     * （`targetBuildVersion`，由 upgradeBridge 接口在下发升级指令时一并返回；见 spec §7.3）
     * 或超时（建议 90s）。轮询期间静默刷新，不打断用户浏览。
     *
     * 2026-08-12 review 修复：原逻辑只判断"变得跟升级前不一样"，如果重连后上报的是另一个
     * 非目标版本（升级失败回退 / 装错包 / 巧合的旧值变化），会被误判为升级成功。改为精确
     * 匹配 targetBuildVersion；调用方在拿不到 targetBuildVersion 时才退回旧的"有变化即算"判断。
     */
    async function pollBridgeUpgradeResult(workspaceUid, previousBuildVersion, targetBuildVersion, timeoutMs = 90_000) {
        const deadline = Date.now() + timeoutMs;
        while (Date.now() < deadline) {
            await new Promise(resolve => setTimeout(resolve, 3000));
            const latest = await fetchData(true);
            syncSelectedWorkspaceFrom(latest, workspaceUid);
            const latestWs = latest.find(w => w.workspaceUid === workspaceUid);
            if (!latestWs || !latestWs.bridgeBuildVersion)
                continue;
            if (targetBuildVersion) {
                if (latestWs.bridgeBuildVersion === targetBuildVersion)
                    return true;
            }
            else if (latestWs.bridgeBuildVersion !== previousBuildVersion) {
                return true;
            }
        }
        return false;
    }
    async function runUpgradeBridgeInBackground(workspaceUid) {
        const wsBefore = workspaces.find(w => w.workspaceUid === workspaceUid);
        const previousBuildVersion = wsBefore?.bridgeBuildVersion;
        setUpgradingWorkspaceIds(prev => new Set(prev).add(workspaceUid));
        const messageKey = `upgrade-bridge-${workspaceUid}`;
        try {
            const rsp = await RestHelper.mustPost(`${API_PREFIX}/upgradeBridge`, {
                workspaceUid,
            });
            const data = rsp.json.data;
            if (data?.alreadyLatest) {
                message.info(ze('当前已是最新版本', 'Already up to date'));
                return;
            }
            if (data?.success === false) {
                message.error(ze(`升级失败：${data.message || '未知错误'}`, `Upgrade failed: ${data.message || 'unknown error'}`));
                return;
            }
            message.loading({
                content: ze('升级指令已接受，等待重新上线…', 'Upgrade accepted, waiting for reconnect…'),
                key: messageKey,
                duration: 0,
            });
            const upgraded = await pollBridgeUpgradeResult(workspaceUid, previousBuildVersion, data?.targetBuildVersion);
            message.destroy(messageKey);
            const finalList = await fetchData(true);
            syncSelectedWorkspaceFrom(finalList, workspaceUid);
            if (upgraded) {
                message.success(ze('连接程序已升级到最新版本', 'Connector upgraded to the latest version'));
            }
            else {
                message.info(ze('升级指令已下发，但尚未看到新版本上线。若长时间未生效，请刷新页面或检查工作区网络后重试。', 'Upgrade command sent, but the new version has not appeared yet. If this persists, refresh the page or check the workspace network.'));
            }
        }
        catch (e) {
            message.destroy(messageKey);
            console.error('Upgrade bridge failed', e);
            message.error(ze('升级请求失败，请稍后重试', 'Upgrade request failed, please retry later'));
        }
        finally {
            setUpgradingWorkspaceIds(prev => {
                const next = new Set(prev);
                next.delete(workspaceUid);
                return next;
            });
        }
    }
    function upgradeBridgeByUid(workspaceUid) {
        const ws = workspaces.find(w => w.workspaceUid === workspaceUid);
        if (!ws)
            return;
        if (ws.healthStatus !== 'HEALTHY') {
            message.warning(ze('工作区离线，无法升级。请确保本地连接程序在线后再试。', 'Workspace is offline. Please ensure the connector is online before upgrading.'));
            return;
        }
        if (!canSelfUpgradeBridge(ws)) {
            message.warning(ze('当前本地连接程序版本不支持一键升级，请使用安装命令手动升一次', 'This connector version does not support one-click upgrade; please use the install command instead'));
            return;
        }
        Modal.confirm({
            title: ze('升级连接程序', 'Upgrade Connector'),
            icon: _jsx(RocketOutlined, { style: { color: '#1890ff' } }),
            content: (_jsxs("div", { children: [_jsx("p", { children: ze('将下载最新的本地连接程序（Bridge）覆盖当前代码并自动重启，过程中会短暂离线（这是正常现象，不代表连接故障）。', 'This will download the latest local connector (Bridge) package to overwrite the current code and restart it automatically. It will be briefly offline during this — this is expected, not a connection failure.') }), _jsx("p", { children: ze('与「重启 Agent」不同：这里更新的是本地连接程序本身，而不是重新加载 MCP 配置。', 'Unlike "Restart Agent", this updates the local connector program itself rather than just reloading the MCP config.') }), _jsx("p", { children: ze('确认继续？', 'Continue?') })] })),
            okText: ze('确认升级', 'Upgrade'),
            cancelText: ze('取消', 'Cancel'),
            onOk: () => {
                void runUpgradeBridgeInBackground(workspaceUid);
            },
        });
    }
    // -------------------- Hermes 工作区日志查看 --------------------
    async function fetchWorkspaceLog(workspaceUid, logKey, lines) {
        setLogModalLoading(true);
        try {
            const rsp = await RestHelper.mustPost(`${API_PREFIX}/getWorkspaceLogTail`, {
                workspaceUid,
                logKey,
                lines,
            });
            const data = rsp.json.data;
            setLogModalContent(data.content || '');
            setLogModalMeta({
                path: data.path,
                exists: data.exists,
                returnedLines: data.returnedLines,
                truncated: data.truncated,
            });
            if (!data.exists) {
                message.info(ze('日志文件尚不存在', 'Log file does not exist yet'));
            }
        }
        catch (e) {
            console.error('fetchWorkspaceLog failed', e);
            setLogModalContent('');
            setLogModalMeta(null);
            message.error(ze('日志加载失败，请确认 Bridge 在线后重试', 'Failed to load logs; ensure Bridge is online'));
        }
        finally {
            setLogModalLoading(false);
        }
    }
    function openLogModal(ws) {
        if (ws.healthStatus !== 'HEALTHY') {
            message.warning(ze('工作区离线，无法查看日志。请确保 Bridge 在线后再试。', 'Workspace is offline. Please ensure Bridge is online before viewing logs.'));
            return;
        }
        setLogModalWorkspace(ws);
        setLogModalKey('agent');
        setLogModalLines(DEFAULT_LOG_TAIL_LINES);
        setLogModalContent('');
        setLogModalMeta(null);
        setLogModalVisible(true);
        void fetchWorkspaceLog(ws.workspaceUid, 'agent', DEFAULT_LOG_TAIL_LINES);
    }
    function closeLogModal() {
        setLogModalVisible(false);
        setLogModalWorkspace(null);
        setLogModalContent('');
        setLogModalMeta(null);
    }
    function handleLogKeyChange(nextKey) {
        setLogModalKey(nextKey);
        if (logModalWorkspace) {
            void fetchWorkspaceLog(logModalWorkspace.workspaceUid, nextKey, logModalLines);
        }
    }
    function handleRefreshLog() {
        if (logModalWorkspace) {
            void fetchWorkspaceLog(logModalWorkspace.workspaceUid, logModalKey, logModalLines);
        }
    }
    function scrollLogToBottom() {
        const el = logScrollRef.current;
        if (!el)
            return;
        el.scrollTop = el.scrollHeight;
    }
    /** 打开 / 刷新 / 切换日志后默认滚到最底部（最新内容） */
    useEffect(() => {
        if (!logModalVisible || logModalLoading)
            return;
        const timerId = window.setTimeout(() => {
            scrollLogToBottom();
        }, 0);
        return () => window.clearTimeout(timerId);
    }, [logModalVisible, logModalLoading, logModalContent, logModalKey]);
    // -------------------- Bridge 安装命令弹窗 --------------------
    function showBridgeCommandModal(bridgeToken, workspaceUid) {
        setModalBridgeToken(bridgeToken);
        setModalWorkspaceUid(workspaceUid);
        setModalBridgeType(DEFAULT_BRIDGE_TYPE);
        setModalLlmApiKey(''); // 每次打开 Modal 都清空，避免上次的 key 残留
        setBridgeCmdVisible(true);
    }
    function handleCopyBridgeCmd() {
        navigator.clipboard.writeText(generateBridgeInstallCommand(modalBridgeToken, getEffectiveBridgeType(modalBridgeType), modalLlmApiKey));
        message.success(ze('已复制到剪贴板', 'Copied to clipboard'));
    }
    // -------------------- AnyDev 一键部署 --------------------
    //
    // 给"没有自己开发机"的内部用户准备的快捷入口：直接调公司 AnyDev 平台创建一台 2C4G 开发容器，
    // 并把当前 Workspace 的 bridge 安装命令塞进容器的 startCmd 让其启动时自动部署。
    //
    // 两处入口共享同一 handler：
    //   - Bridge 安装命令 Modal（Workspace 刚创建完弹出的那个）
    //   - Workspace 卡片展开后的"安装命令"区域
    //
    // UX 要点：
    //   1. 点击后先弹 Modal.confirm 二次确认（避免误点消耗 AnyDev 配额）
    //   2. handler 调用期间禁用按钮 + 显示 loading（5~30s 不等）
    //   3. AnyDev 接口本期不解析返回结构，直接把 rawResponseBody 透传展示给开发者排错
    /** AnyDev 一键部署调用过程中是否处于 loading 状态（按钮共享） */
    const [anydevDeploying, setAnydevDeploying] = useState(false);
    /** AnyDev 部署完成后展示返回详情的弹窗（透传 rawResponseBody） */
    const [anydevResultVisible, setAnydevResultVisible] = useState(false);
    const [anydevResultData, setAnydevResultData] = useState(null);
    /**
     * 触发 AnyDev 一键部署。
     *
     * @param workspaceUid  要部署的 Workspace 业务 ID
     * @param _bridgeType   已忽略；AnyDev 暂时固定部署 Hermes
     * @param llmApiKey     用户填写的 Venus secretKey（必填）
     */
    function handleDeployToAnydev(workspaceUid, _bridgeType, llmApiKey) {
        if (!workspaceUid)
            return;
        // 使用工作区已存储的 agentType，不再硬编码
        const ws = workspaces.find(w => w.workspaceUid === workspaceUid);
        const bridgeType = ws?.agentType || 'hermes';
        // 用户点击链接的瞬间立即关闭工作区详情 Modal / Bridge 命令 Modal，避免二次确认 Modal
        // 套在原 Modal 上方造成"两层弹窗叠加"的视觉混乱。即使后续因为 key 校验弹 warning，
        // 用户也能更聚焦在 warning 内容上。
        closeModal();
        setBridgeCmdVisible(false);
        const trimmedKey = (llmApiKey || '').trim();
        // Hermes 没填 key 直接拦截：容器拉起后 install.sh 会在 Step 4 因 LLM_API_KEY 为空而 exit 1，
        // 用户必须 SSH 进去手动跑，违背"一键部署"语义。提前给清晰错误比让容器跑不起来更好。
        if (!trimmedKey) {
            Modal.warning({
                title: ze('请先填写 LLM API Key', 'Please enter LLM API Key first'),
                content: (_jsxs("div", { children: [_jsx("p", { children: ze('Hermes 需要 LLM Provider 凭据才能完成自动初始化。请在上方"LLM API Key (Venus)"输入框中填入你的 Venus secretKey 后再点击此按钮。', 'The Hermes variant requires LLM Provider credentials to complete auto-initialization. Please fill the "LLM API Key (Venus)" field above before clicking this button.') }), _jsxs("p", { style: { marginTop: 8 }, children: [ze('还没有 Venus Key？', 'Don\'t have a Venus key yet?'), ' ', _jsx("a", { href: VENUS_PERSONAL_ACCOUNT_URL, target: "_blank", rel: "noopener noreferrer", children: ze('前往 Venus 个人账号页面获取', 'Open the Venus personal account page') })] })] })),
            });
            return;
        }
        Modal.confirm({
            title: ze('一键创建 AnyDev 开发容器', 'Create AnyDev Development Container'),
            icon: _jsx(ThunderboltOutlined, { style: { color: '#1890ff' } }),
            content: (_jsxs("div", { children: [_jsx("p", { children: ze('即将为你在公司内部 AnyDev 平台创建一台 2 核 4GB 的开发容器，并自动部署 Bridge 程序。整个过程预计 5~10 分钟，容器拉起后 Workspace 会自动变为在线状态。', 'A 2-core 4GB AnyDev development container will be created and the Bridge will be auto-deployed. This usually takes 5~10 minutes; the Workspace will turn online automatically after the container boots up.') }), trimmedKey && (_jsx("p", { style: { marginTop: 8, color: '#52c41a' }, children: ze('✅ 已填写 LLM API Key，install.sh 将自动完成 Hermes Provider 配置，无需 SSH 进容器手动操作。', '✅ LLM API Key provided. install.sh will auto-configure the Hermes Provider, no manual SSH step required.') })), _jsx("p", { children: ze('确认继续？', 'Continue?') })] })),
            okText: ze('确认创建', 'Create'),
            cancelText: ze('取消', 'Cancel'),
            onOk: () => {
                // ⚠️ 设计要点：AnyDev 接口实测整个链路耗时较长（容器调度 + 镜像拉取 + bridge 安装），
                // 部分场景能到 5~10 分钟。直接 `await` 会让 Modal 一直转圈、Workspace 操作面板假死，
                // 体验非常差且用户会反复点。
                //
                // 改为"乐观提示 + 后台 fire-and-forget"：
                //   1) onOk 立即返回（不 await），Modal 立刻关闭
                //   2) 顺手关掉"工作区创建成功"详情 Modal —— 用户已经触发了创建，
                //      详情面板没必要继续占着屏幕，让他们能立刻去看 Workspace 卡片的健康状态
                //   3) 立刻 toast 一条"已一键创建，预计 5-10 分钟完成初始化"
                //   4) 后台异步发请求；接口最终回来后再用 message + setAnydevResultData 展示真实结果
                //      (成功 → 安静更新 anydevResultData，可在结果查看 Modal 里看；失败 → toast 报错)
                //
                // 这样用户感知就是"点一下立刻有反馈"，可以继续做别的事；真实状态由 Workspace 卡片
                // 上的 health 心跳指示（容器内 Bridge 上线后会自动变绿）。
                // Bridge 命令 Modal / 工作区详情 Modal 已在 handleDeployToAnydev 入口处统一关闭，
                // 这里只需要发起后台请求即可。
                message.success(ze('已一键创建 AnyDev 容器，预计 5~10 分钟完成初始化。容器内 Bridge 上线后此 Workspace 状态会自动变绿，可关闭此页面继续其他操作。', 'AnyDev container creation triggered. Initialization usually takes 5~10 minutes. The Workspace status will turn green automatically after the Bridge connects; you can close this page and continue with other tasks.'), 6);
                // 后台异步执行实际请求 —— 注意：这里故意不 await，让 Modal 立即关闭。
                // 用 IIFE 包裹避免在 onOk 返回值里返回 Promise（否则 antd Modal 仍会等 Promise resolve）。
                (async () => {
                    setAnydevDeploying(true);
                    try {
                        // ⚠️ 关键：直接把前端生成好的安装命令传给后端。
                        //
                        // 原因：后端原先从 HTTP Host 头取 serverHost，但请求经过 k8s Service / nginx 转发后，
                        // Host 头会被改写为内部 service 名（如 cs-api-server），导致 install.sh 的
                        // --server 参数错误，AnyDev 容器内 Bridge 无法连回 CrashSight。
                        //
                        // 改为前端把基于 window.location.hostname 拼好的命令直接传过去（用户在弹窗里
                        // 看到的就是这条），后端原样下发给 AnyDev startCmd（仅做"多行合并为单行"的规范化），
                        // 不做二次拼装。这样：
                        //   1. --server 永远是用户当前访问的真实域名
                        //   2. 命令所见即所得（"安装命令展示框"和实际 startCmd 一致）
                        //   3. 切换 Hermes/CodeBuddy + key 拼接逻辑统一在前端 generateBridgeInstallCommand 里维护
                        //
                        // 我们仍然把 bridgeType / llmApiKey 也带上，作为兼容老后端版本的副字段（无害）。
                        const bridgeToken = workspaces.find(w => w.workspaceUid === workspaceUid)?.bridgeToken || '';
                        const installCommand = bridgeToken
                            ? generateBridgeInstallCommand(bridgeToken, bridgeType, trimmedKey)
                            : undefined;
                        const rsp = await RestHelper.mustPost(`${API_PREFIX}/deployToAnydev`, {
                            workspaceUid,
                            bridgeType,
                            // 后端优先使用 installCommand 拼 startCmd；不传时 fallback 到老的 bridgeType + llmApiKey 拼装路径。
                            installCommand,
                            // 兼容字段：老后端版本（未支持 installCommand）会从这里取 key 自己拼命令。
                            // 后端如果暂未升级仍可兼容（多收一个未识别字段不影响）。
                            llmApiKey: trimmedKey || undefined,
                            // 2026-08-13 补充：即便正常情况下 installCommand 已经带了正确的 --server，仍顺手把
                            // window.location.hostname 传一份——保护 installCommand 为空时后端 fallback 拼装
                            // 逻辑判定测试服/正式服 CDN 目录用的 serverHost（同 upgradeBridge 那次踩坑，见后端
                            // DeployToAnydevRequest#serverHost 注释），不依赖 HTTP Host 头。
                            serverHost: window.location.hostname,
                        });
                        const data = rsp.json.data;
                        // 静默更新结果数据：用户如果想看 startCmd / rawResponse 详情，可以在卡片上点"查看结果"
                        // （或日后我们加个按钮）。这里不再自动弹结果 Modal，避免在用户已经切换到其他页面后
                        // 突然弹一个跟当前上下文无关的 Modal 打断他。
                        setAnydevResultData(data);
                        if (!data.success) {
                            message.error(ze(`AnyDev 接口返回失败（HTTP ${data.statusCode}），请联系管理员排查`, `AnyDev API returned failure (HTTP ${data.statusCode}), please contact admin`));
                            // 失败时还是弹 Modal，让用户能看到 rawResponse 排查
                            setAnydevResultVisible(true);
                        }
                    }
                    catch (e) {
                        console.error('Deploy to AnyDev failed', e);
                        message.error(ze(`调用 AnyDev 失败：${e?.message || e}`, `Deploy to AnyDev failed: ${e?.message || e}`));
                    }
                    finally {
                        setAnydevDeploying(false);
                    }
                })();
            },
        });
    }
    // -------------------- 一键创建（Quick Setup） --------------------
    /**
     * 一键创建全流程：MCP → Agent → Workspace，无需用户填写任何表单。
     * 流程：
     *   1. 检测是否已有 CrashSight MCP（通过 URL 匹配），没有则自动申请令牌并创建
     *   2. 创建默认 Agent（空 MCP/Skills 配置 = 运行时使用全部已启用的资源）
     *   3. 创建默认 Workspace 并关联上一步的 Agent
     *   4. 完成后弹出 Bridge 安装命令弹窗
     */
    async function handleQuickSetup() {
        if (quickCreating)
            return;
        setQuickCreating(true);
        try {
            // Step 1: 检测是否已有 CrashSight MCP（用户可能已手动配置过）
            const hasCrashSightMcp = mcpServers.some(s => {
                const url = s.serverConfig?.url || '';
                return isCrashSightMcpUrl(url);
            });
            if (!hasCrashSightMcp) {
                setQuickCreateStep(ze('正在创建 CrashSight MCP…', 'Creating CrashSight MCP…'));
                // 令牌管理逻辑与 useQuickAddCrashSightMcp hook 一致：查找/创建/续期名为 "CrashClaw" 的 MCP 电子令牌
                const MCP_TOKEN_API = '/api/mcp';
                const TOKEN_NAME = 'CrashClaw';
                // 后端 createToken / 单次 renewToken 上限均为 365 天，超过会直接被拒
                const TOKEN_EXPIRE_DAYS = 360;
                const { json: tokenListJson } = await RestHelper.post(`${MCP_TOKEN_API}/listTokens`);
                const tokens = tokenListJson.data || [];
                let targetToken = tokens.find((t) => t.name === TOKEN_NAME) || null;
                if (!targetToken) {
                    const { json: createJson } = await RestHelper.post(`${MCP_TOKEN_API}/createToken`, {
                        name: TOKEN_NAME,
                        expireDays: TOKEN_EXPIRE_DAYS,
                    });
                    targetToken = createJson.data;
                }
                else if (targetToken.expiresAtMillis < Date.now()) {
                    await RestHelper.post(`${MCP_TOKEN_API}/renewToken`, {
                        id: targetToken.id,
                        expireDays: TOKEN_EXPIRE_DAYS,
                    });
                }
                if (!targetToken?.apiKey) {
                    throw new Error('Failed to obtain CrashClaw token');
                }
                const mcpNames = new Set(mcpServers.map(s => s.serverName));
                const mcpName = generateUniqueName('CrashSight MCP', mcpNames);
                await RestHelper.mustPost(`${API_PREFIX}/createMcpServer`, {
                    serverName: mcpName,
                    transportType: 'STREAMABLE_HTTP',
                    serverConfig: {
                        headers: { 'X-CSMCP-API-KEY': targetToken.apiKey },
                        timeout: 60,
                        transportType: 'streamable-http',
                        url: getCrashSightMcpUrl(),
                    },
                    description: null,
                });
            }
            // Step 2: 创建默认 Agent
            setQuickCreateStep(ze('正在创建默认 Agent…', 'Creating default Agent…'));
            // 刷新 agents 列表以获取最新数据来判断重名
            const agentListRsp = await RestHelper.mustPost(`${API_PREFIX}/listAgents`);
            const latestAgents = agentListRsp.json.data || [];
            const agentNames = new Set(latestAgents.map((a) => a.agentName));
            const agentName = generateUniqueName(ze('我的 Agent', 'My Agent'), agentNames);
            // 空数组表示不限定，运行时将使用用户全部已启用的 MCP Server 和 Skills（后端 getAgentConfig 兜底逻辑）
            const agentRsp = await RestHelper.mustPost(`${API_PREFIX}/createAgent`, {
                agentName,
                description: null,
                systemPrompt: CRASHCLAW_DEFAULT_SYSTEM_PROMPT,
                agentConfig: {
                    mcpServerUids: [],
                    skillUids: [],
                },
            });
            const createdAgentUid = agentRsp.json.data.agentUid;
            // Step 3: 创建默认 Workspace
            setQuickCreateStep(ze('正在创建工作区…', 'Creating Workspace…'));
            const wsNames = new Set(workspaces.map(ws => ws.workspaceName));
            const wsName = generateUniqueName(ze('我的工作区', 'My Workspace'), wsNames);
            const wsRsp = await RestHelper.mustPost(`${API_PREFIX}/createWorkspace`, {
                workspaceName: wsName,
                agentUid: createdAgentUid,
                description: null,
            });
            const createdWs = wsRsp.json.data;
            message.success(ze('一键创建完成！', 'Quick setup completed!'));
            await fetchData();
            notifyCrashClawWorkspacesChanged();
            if (createdWs?.workspaceUid) {
                testWorkspaceByUid(createdWs.workspaceUid);
            }
            // 弹出 Bridge 安装命令
            if (createdWs?.bridgeToken) {
                showBridgeCommandModal(createdWs.bridgeToken, createdWs.workspaceUid);
            }
        }
        catch (e) {
            console.error('Quick setup failed', e);
            message.error(ze('一键创建失败，请稍后重试', 'Quick setup failed, please try again later'));
        }
        finally {
            setQuickCreating(false);
            setQuickCreateStep('');
        }
    }
    // -------------------- Modal lifecycle --------------------
    function openCreateModal() {
        form.resetFields();
        form.setFieldsValue({ llmBaseUrl: DEFAULT_LLM_BASE_URL });
        setModalMode('create');
        setSelectedWorkspace(null);
        setCreateEnvType('anydev');
        setModalVisible(true);
    }
    function openPreviewModal(ws) {
        setSelectedWorkspace(ws);
        setModalMode('preview');
        setInlineNameEditing(false);
        setInlineNameDraft('');
        setModalVisible(true);
        // 管理者看不到所有者 Agent，预取工作区模型以正确展示下拉当前值
        if (!isWorkspaceOwner(ws) && canManageWorkspace(ws) && ws.agentUid && !workspaceModelMap[ws.workspaceUid]) {
            RestHelper.mustPost(`${API_PREFIX}/getWorkspaceModel`, {
                workspaceUid: ws.workspaceUid,
            }).then((rsp) => {
                const model = rsp?.json?.data?.model;
                if (typeof model === 'string' && model) {
                    setWorkspaceModelMap(prev => ({ ...prev, [ws.workspaceUid]: model }));
                }
            }).catch(() => { });
        }
        // 先清空上一个工作区的仓库列表，避免切换预览时短暂闪现上一个工作区的数据
        setRepoBindings([]);
        setRepoBindingsLoading(true);
        void loadRepoBindings(ws.workspaceUid);
    }
    const openedInitialWorkspaceRef = useRef(false);
    useEffect(() => {
        if (!initialWorkspaceUid || openedInitialWorkspaceRef.current || loading)
            return;
        const ws = workspaces.find(w => w.workspaceUid === initialWorkspaceUid);
        if (ws) {
            openedInitialWorkspaceRef.current = true;
            openPreviewModal(ws);
        }
    }, [initialWorkspaceUid, workspaces, loading]);
    function switchToEditMode() {
        if (!selectedWorkspace)
            return;
        form.resetFields();
        form.setFieldsValue({
            workspaceName: selectedWorkspace.workspaceName,
            description: selectedWorkspace.description || '',
        });
        setModalMode('edit');
    }
    /**
     * 从列表卡片直接唤起编辑弹窗（卡片头铅笔图标 / 「更多操作」下拉「编辑」项），
     * 不需要像 switchToEditMode 那样先经过预览态。所有者或共享管理者（MANAGER）可编辑，
     * 权限口径与详情弹窗底部「编辑」按钮（switchToEditMode / previewCanManage）保持一致。
     */
    function openEditModalDirect(ws) {
        if (!canManageWorkspace(ws))
            return;
        setSelectedWorkspace(ws);
        form.resetFields();
        form.setFieldsValue({
            workspaceName: ws.workspaceName,
            description: ws.description || '',
        });
        setModalMode('edit');
        setModalVisible(true);
    }
    function closeModal() {
        setModalVisible(false);
        setSelectedWorkspace(null);
        setModalMode('create');
        setInlineNameEditing(false);
        setInlineNameDraft('');
        setModalBridgeToken('');
        setModalLlmApiKey('');
        setModalBridgeType(DEFAULT_BRIDGE_TYPE);
        // 2026-08-09 review 修复：RepoAuthorizeModal 是从这个详情弹窗内部拉起的嵌套弹窗，
        // 关闭详情弹窗时 selectedWorkspace 被清空，如果不顺手关掉它，它会带着空的
        // workspaceUid/bridgeOnline 变成孤儿状态继续挂在页面上，后续的 listGiteeRepos /
        // gongfengOauthAuthorize / createGitCredential 等调用全都会打到一个不存在的工作区上
        setRepoAuthorizeModalVisible(false);
    }
    // -------------------- Create --------------------
    /**
     * 按当前环境类型分发：
     *   - anydev：走 handleCreateAnyDevWorkspace
     *   - cvm   ：先 createWorkspace，再弹出 Bridge 安装命令（与 master 一致）
     */
    function handleCreateSubmit() {
        if (createEnvType === 'anydev') {
            return handleCreateAnyDevWorkspace();
        }
        return handleCreateCvmWorkspace();
    }
    const WORKSPACE_NAME_CONFLICT_PREFIX = 'Workspace name already exists';
    /**
     * 统一处理"创建工作区"接口失败的展示逻辑，按后端 errorCode/message/HTTP 状态分档：
     *   档1 重名（400 + IllegalArgument + message 以 "Workspace name already exists" 开头）
     *       → 名称输入框红字 + 红框 + 自动聚焦 + 顶部 Toast 同步展示
     *   档2 其他 400 IllegalArgument → 顶部 Toast 透传后端 message 原文
     *   档3 401/403 → 顶部 Toast 固定的鉴权失败文案
     *   档4 其他（5xx/网络异常/未识别） → 顶部 Toast 兜底文案
     * 注：createWorkspace/createAnyDevWorkspace 请求已通过 silentErrorCodes 屏蔽 RestHelper 全局的
     * 右上角 notification，避免和这里的 Toast 重复展示。
     */
    function showWorkspaceCreateError(e) {
        const err = (e || {});
        const { status, errorCode } = err;
        const backendMessage = err.message || err.json?.message || '';
        if (errorCode === ApiErrorCode.IllegalArgument && backendMessage.startsWith(WORKSPACE_NAME_CONFLICT_PREFIX)) {
            // 从 "Workspace name already exists: xxx" 中截取冒号后的名称部分；解析不出来则用不带具体名称的兜底文案
            const conflictName = backendMessage.slice(backendMessage.indexOf(':') + 1).trim();
            const conflictText = conflictName
                ? ze(`工作区名称"${conflictName}"已存在，请换个名称`, `Workspace name "${conflictName}" already exists, please choose another one`)
                : ze('工作区名称已存在，请换个名称', 'Workspace name already exists, please choose another one');
            form.setFields([{ name: 'workspaceName', errors: [conflictText] }]);
            message.error(conflictText);
            // Form.Item 默认 validateTrigger 为 onChange，用户修改名称时会重新触发校验并自然清除这里手动注入的错误
            const fieldInstance = form.getFieldInstance('workspaceName');
            if (fieldInstance && typeof fieldInstance.focus === 'function') {
                fieldInstance.focus();
            }
            return;
        }
        if (errorCode === ApiErrorCode.IllegalArgument) {
            message.error(backendMessage || ze('参数不合法，请检查后重试', 'Invalid parameters, please check and retry'));
            return;
        }
        if (status === 401 || status === 403) {
            message.error(ze('身份鉴权失败，请刷新页面重新登录', 'Authentication failed, please refresh the page and log in again'));
            return;
        }
        // 2026-08-12 review 修复：AnyDev 部署失败时（createAnyDevWorkspace 会回滚工作区并以
        // GeneralReject 报错），此前这里只认 IllegalArgument 才展示后端 message，
        // 其它错误码一律显示通用文案，用户看不到"AnyDev 容器创建失败：xxx"这类具体原因。
        // GeneralReject / 其它已知业务错误码都优先展示后端返回的 message（更具体，更利于自助排查）。
        if (errorCode === ApiErrorCode.GeneralReject && backendMessage) {
            message.error(backendMessage);
            return;
        }
        message.error(backendMessage || ze('创建失败，请稍后重试', 'Creation failed, please retry later'));
    }
    /** 确保用户有默认 Agent（已有则复用第一个，否则创建空配置 Agent） */
    async function ensureDefaultAgentUid() {
        const agentListRsp = await RestHelper.mustPost(`${API_PREFIX}/listAgents`);
        const agents = agentListRsp.json.data || [];
        if (agents.length > 0) {
            return agents[0].agentUid;
        }
        const agentNames = new Set(agents.map(a => a.agentName));
        const agentName = generateUniqueName(ze('默认 Agent', 'Default Agent'), agentNames);
        const agentRsp = await RestHelper.mustPost(`${API_PREFIX}/createAgent`, {
            agentName,
            description: null,
            systemPrompt: CRASHCLAW_DEFAULT_SYSTEM_PROMPT,
            agentConfig: {
                mcpServerUids: [],
                skillUids: [],
            },
        });
        return agentRsp.json.data.agentUid;
    }
    /**
     * CVM / 开发机创建工作区：先落库 Workspace + bridgeToken，再展示安装命令。
     */
    async function handleCreateCvmWorkspace() {
        try {
            const values = await form.validateFields(['workspaceName', 'description', 'agentType']);
            setSubmitting(true);
            const agentUid = await ensureDefaultAgentUid();
            await ensureCrashSightMcp();
            const rsp = await RestHelper.mustPost(`${API_PREFIX}/createWorkspace`, {
                workspaceName: values.workspaceName,
                description: values.description || null,
                agentUid,
                agentType: values.agentType || 'hermes',
            }, { silentErrorCodes: [ApiErrorCode.IllegalArgument] });
            const createdWs = rsp.json.data;
            message.success(ze('工作区创建成功', 'Workspace created'));
            closeModal();
            await fetchData();
            notifyCrashClawWorkspacesChanged();
            if (createdWs?.workspaceUid) {
                testWorkspaceByUid(createdWs.workspaceUid);
            }
            if (createdWs?.bridgeToken) {
                showBridgeCommandModal(createdWs.bridgeToken, createdWs.workspaceUid);
            }
        }
        catch (e) {
            console.error(e);
            if (e && typeof e === 'object' && 'errorFields' in e)
                return;
            showWorkspaceCreateError(e);
        }
        finally {
            setSubmitting(false);
        }
    }
    /**
     * AnyDev 一键创建链路（无需用户填 API Key）：
     *   1. POST createAnyDevWorkspace：后端编排 默认 Agent + Workspace + AnyDev 容器部署
     *   2. 自动配置 CrashSight MCP（已有则跳过）
     *
     * 注：Agent 绑定与容器部署由后端 createAnyDevWorkspace 一次完成，前端不再单独调 createAgent/createWorkspace。
     */
    async function handleCreateAnyDevWorkspace() {
        try {
            const values = await form.validateFields(['workspaceName', 'description', 'agentType', 'llmApiKey', 'llmBaseUrl']);
            const trimmedKey = (values.llmApiKey || '').trim();
            if (!trimmedKey) {
                message.warning(ze('请先填写 Venus secretKey', 'Please enter Venus secretKey first'));
                return;
            }
            setAnydevCreating(true);
            // Step 1: 后端编排 Agent + Workspace + AnyDev 部署
            setAnydevCreateStep(ze('正在创建工作区与 AnyDev 容器…', 'Creating workspace and AnyDev container…'));
            const wsRsp = await RestHelper.mustPost(`${API_PREFIX}/createAnyDevWorkspace`, {
                workspaceName: values.workspaceName,
                description: values.description || null,
                agentType: values.agentType || 'hermes',
                llmApiKey: trimmedKey,
                llmBaseUrl: (values.llmBaseUrl || DEFAULT_LLM_BASE_URL).trim(),
                serverHost: window.location.hostname,
            }, { silentErrorCodes: [ApiErrorCode.IllegalArgument] });
            const created = wsRsp.json.data;
            // Step 2: 自动配置 CrashSight MCP（已有则跳过）
            setAnydevCreateStep(ze('正在配置 CrashSight MCP…', 'Configuring CrashSight MCP…'));
            await ensureCrashSightMcp();
            // 2026-08-12 review 修复：后端 createAnyDevWorkspace 现在会在 AnyDev 提交失败时把刚
            // 创建的工作区一并回滚并整体报错（走下面的 catch 分支），不会再返回
            // anydevDeploySuccess=false 的"半成品成功响应"——因为工作区卡片上原先承诺的"重试部署"
            // 入口只在非 AnyDev 类型时才渲染，对 AnyDev 类型的失败工作区其实无法补救，不如直接
            // 回滚保证创建操作的原子性。这里的 if 分支理论上不会再触发，保留仅作兜底。
            if (created.anydevDeploySuccess === false) {
                message.warning(ze('工作区已创建，但 AnyDev 容器提交失败，请删除后重试', 'Workspace created but AnyDev deploy failed. Please delete it and retry.'));
            }
            else {
                message.success(ze('工作区已创建，AnyDev 容器正在初始化（约 5~10 分钟）。Bridge 上线后状态会自动变绿。', 'Workspace created. AnyDev container is initializing (~5–10 min). Status will turn green when Bridge connects.'), 6);
            }
            closeModal();
            await fetchData();
            notifyCrashClawWorkspacesChanged();
            // Bridge 上线需 5~10 分钟，由 30s 轮询 testWorkspace 自动刷新在线状态
        }
        catch (e) {
            console.error(e);
            if (e && typeof e === 'object' && 'errorFields' in e)
                return;
            showWorkspaceCreateError(e);
        }
        finally {
            setAnydevCreating(false);
            setAnydevCreateStep('');
        }
    }
    /**
     * 确保 CrashSight MCP 已存在；若没有则自动申请令牌并创建。
     * 内部复用 handleQuickSetup 中的逻辑（直接拷贝以避免大改 quick setup）。
     */
    async function ensureCrashSightMcp() {
        const hasCrashSightMcp = mcpServers.some(s => {
            const url = s.serverConfig?.url || '';
            return isCrashSightMcpUrl(url);
        });
        if (hasCrashSightMcp)
            return;
        const MCP_TOKEN_API = '/api/mcp';
        const TOKEN_NAME = 'CrashClaw';
        // 后端 createToken / 单次 renewToken 上限均为 365 天，超过会直接被拒
        const TOKEN_EXPIRE_DAYS = 360;
        const { json: tokenListJson } = await RestHelper.post(`${MCP_TOKEN_API}/listTokens`);
        const tokens = tokenListJson.data || [];
        let targetToken = tokens.find((t) => t.name === TOKEN_NAME) || null;
        if (!targetToken) {
            const { json: createJson } = await RestHelper.post(`${MCP_TOKEN_API}/createToken`, {
                name: TOKEN_NAME,
                expireDays: TOKEN_EXPIRE_DAYS,
            });
            targetToken = createJson.data;
        }
        else if (targetToken.expiresAtMillis < Date.now()) {
            await RestHelper.post(`${MCP_TOKEN_API}/renewToken`, {
                id: targetToken.id,
                expireDays: TOKEN_EXPIRE_DAYS,
            });
        }
        if (!targetToken?.apiKey) {
            throw new Error('Failed to obtain CrashClaw token');
        }
        const mcpNames = new Set(mcpServers.map(s => s.serverName));
        const mcpName = generateUniqueName('CrashSight MCP', mcpNames);
        await RestHelper.mustPost(`${API_PREFIX}/createMcpServer`, {
            serverName: mcpName,
            transportType: 'STREAMABLE_HTTP',
            serverConfig: {
                headers: { 'X-CSMCP-API-KEY': targetToken.apiKey },
                timeout: 60,
                transportType: 'streamable-http',
                url: getCrashSightMcpUrl(),
            },
            description: null,
        });
    }
    // -------------------- Edit --------------------
    async function handleEditSubmit() {
        if (!selectedWorkspace)
            return;
        try {
            const values = await form.validateFields();
            setSubmitting(true);
            await RestHelper.mustPost(`${API_PREFIX}/updateWorkspace`, {
                workspaceUid: selectedWorkspace.workspaceUid,
                workspaceName: values.workspaceName,
                // 简化后前端不再暴露 agentUid 编辑；保持原绑定不变
                agentUid: selectedWorkspace.agentUid,
                description: values.description || null,
            });
            message.success(ze('工作区更新成功', 'Workspace updated'));
            closeModal();
            await fetchData();
            notifyCrashClawWorkspacesChanged();
            // 编辑成功后自动触发一次连通性测试
            testWorkspaceByUid(selectedWorkspace.workspaceUid);
        }
        catch (e) {
            console.error(e);
            if (e && typeof e === 'object' && 'errorFields' in e)
                return;
            message.error(ze('更新失败', 'Update failed'));
        }
        finally {
            setSubmitting(false);
        }
    }
    // -------------------- Delete --------------------
    /**
     * 删除工作区，默认删详情弹窗当前选中的工作区；
     * 从列表卡片「更多操作」下拉直接删除时会传入 target，跳过预览态。
     */
    function handleDeleteWorkspace(target) {
        const ws = target || selectedWorkspace;
        if (!ws)
            return;
        const { workspaceUid, workspaceName } = ws;
        Modal.confirm({
            title: ze('确认删除', 'Confirm Deletion'),
            icon: _jsx(ExclamationCircleOutlined, {}),
            content: ze(`确认要删除工作区 "${workspaceName}" 吗？关联的 Agent 和 MCP Server 不会受影响。`, `Are you sure you want to delete workspace "${workspaceName}"? Associated Agents and MCP Servers will not be affected.`),
            okText: ze('删除', 'Delete'),
            okButtonProps: { danger: true },
            cancelText: ze('取消', 'Cancel'),
            onOk: async () => {
                await RestHelper.mustPost(`${API_PREFIX}/deleteWorkspace`, { workspaceUid });
                message.success(ze('已删除', 'Deleted'));
                // 只有被删除的正是当前弹窗里展示的工作区时才关闭弹窗，避免从卡片「更多操作」删除
                // 工作区 B 时，误把当前正打开的工作区 A 详情/编辑弹窗关掉。
                if (modalVisible && selectedWorkspace?.workspaceUid === workspaceUid)
                    closeModal();
                fetchData();
                notifyCrashClawWorkspacesChanged();
            },
        });
    }
    // -------------------- Render: Card --------------------
    function renderWorkspaceCard(ws) {
        const isTesting = testingWorkspaceIds.has(ws.workspaceUid);
        const isRestarting = restartingWorkspaceIds.has(ws.workspaceUid);
        const owned = isWorkspaceOwner(ws);
        const canManage = canManageWorkspace(ws);
        const displayName = getWorkspaceDisplayName(ws);
        const machineIp = formatMachineIp(ws);
        const creatorName = ws.ownerRtx || ws.ownerLocalUserId || '--';
        const typeLabel = ws.envType === 'anydev' ? 'AnyDev' : ws.envType === 'cvm' ? 'CVM' : '--';
        const moreActionsMenu = (_jsxs(Menu, { onClick: (info) => info.domEvent.stopPropagation(), children: [canManage && (_jsx(Menu.Item, { onClick: () => openEditModalDirect(ws), children: ze('编辑', 'Edit') }, "edit")), _jsx(Menu.Item, { onClick: () => openPreviewModal(ws), children: ze('详情', 'Details') }, "detail"), owned && (_jsx(Menu.Item, { danger: true, onClick: () => handleDeleteWorkspace(ws), children: ze('删除', 'Delete') }, "delete"))] }));
        return (_jsxs("div", { className: style.skillCard, onClick: () => openPreviewModal(ws), children: [_jsxs("div", { className: style.skillCardBody, children: [_jsxs("div", { className: style.skillCardHeader, children: [_jsxs("div", { className: style.skillCardTitleRow, children: [_jsx("span", { className: style.skillCardIcon, children: "\u25C6" }), _jsx("h3", { className: style.skillCardName, children: displayName }), canManage && (_jsx("button", { className: style.actionBtn, title: ze('编辑工作区', 'Edit workspace'), onClick: (e) => {
                                                e.stopPropagation();
                                                openEditModalDirect(ws);
                                            }, children: _jsx(EditOutlined, { style: { fontSize: 13 } }) })), ws.isDefault && owned && (_jsx(Tag, { color: "blue", className: style.sourceTag, children: ze('默认', 'Default') })), !owned && (_jsx(Tag, { color: "purple", className: style.sourceTag, children: ze('共享', 'Shared') }))] }), _jsxs("div", { className: style.workspaceCardHeaderRight, children: [_jsx(Tag, { className: style.sourceTag, children: typeLabel }), _jsxs("div", { className: style.skillCardStatus, children: [isTesting ? (_jsx(LoadingOutlined, { className: style.statusTesting })) : (_jsxs(_Fragment, { children: [_jsx("span", { className: `${style.skillStatusDot} ${getHealthDotClass(ws.healthStatus)}` }), _jsx("span", { className: `${style.skillStatusText} ${getHealthTextClass(ws.healthStatus)}`, children: getHealthText(ws.healthStatus) })] })), (() => {
                                                    const tagKind = getWorkspaceUpgradeTagKind(ws);
                                                    if (!tagKind)
                                                        return null;
                                                    return (_jsx(Tag, { color: "orange", style: { marginLeft: 6 }, title: ze('本地连接程序（Bridge）版本过旧或缺能力，详情内可一键升级', 'The local connector (Bridge) is outdated or missing a capability; upgrade from the detail dialog'), children: tagKind === 'hard' ? ze('待升级', 'Upgrade needed') : ze('可升级', 'Update available') }));
                                                })()] }), _jsxs("span", { className: style.workspaceScopeRow, children: [!owned ? (_jsx("span", { className: `${style.workspaceScopeTag} ${style.workspaceScopeTagTeam}`, children: ze('他人共享', 'Shared with me') })) : sharingScopeMap[ws.workspaceUid] === 'team' ? (_jsx("span", { className: `${style.workspaceScopeTag} ${style.workspaceScopeTagTeam}`, children: ze('团队可见', 'Shared with team') })) : (_jsx("span", { className: `${style.workspaceScopeTag} ${style.workspaceScopeTagSelf}`, children: ze('仅自己', 'Only me') })), canManage && (_jsx("button", { className: style.actionBtn, title: ze('编辑共享范围', 'Edit sharing scope'), onClick: (e) => {
                                                        e.stopPropagation();
                                                        openSharingModalForWorkspace(ws);
                                                    }, children: _jsx(EditOutlined, { style: { fontSize: 13 } }) }))] })] })] }), _jsxs("div", { className: style.workspaceCardInfoGrid, children: [_jsxs("div", { className: style.workspaceCardInfoCol, children: [_jsx("div", { className: style.workspaceCardInfoColLabel, children: ze('机器 IP / 环境 ID', 'Machine IP / Env ID') }), _jsx("div", { className: style.workspaceCardInfoMain, children: machineIp || '--' }), _jsx("div", { className: style.workspaceCardInfoSub, children: formatEnvId(ws) || '--' })] }), _jsxs("div", { className: style.workspaceCardInfoCol, children: [_jsx("div", { className: style.workspaceCardInfoColLabel, children: ze('创建人', 'Creator') }), _jsx("div", { className: style.workspaceCardInfoMain, children: creatorName }), _jsx("div", { className: style.workspaceCardInfoSub, children: formatRelativeTime(ws.createTime) })] }), _jsxs("div", { className: style.workspaceCardInfoCol, children: [_jsx("div", { className: style.workspaceCardInfoColLabel, children: ze('目录 / 代码仓', 'Directory / Repo') }), _jsx("div", { className: style.workspaceCardInfoMain, children: "--" })] })] })] }), _jsx("div", { className: style.agentCardFooterInfo, children: _jsxs("span", { style: { marginLeft: 'auto', display: 'flex', gap: 4 }, children: [canManage && (getCardBridgeType(ws.workspaceUid) === 'hermes' ? (_jsx("button", { className: style.actionBtn, title: ze('查看日志', 'View logs'), disabled: ws.healthStatus !== 'HEALTHY', onClick: (e) => {
                                    e.stopPropagation();
                                    openLogModal(ws);
                                }, children: _jsx(BookOutlined, { style: { fontSize: 13 } }) })) : (_jsx("button", { className: `${style.actionBtn} ${isRestarting ? style.spinIcon : ''}`, title: ze('重启 Agent（重新加载 MCP）', 'Restart Agent (reload MCP)'), disabled: isRestarting || ws.healthStatus !== 'HEALTHY', onClick: (e) => {
                                    e.stopPropagation();
                                    if (!isRestarting)
                                        restartWorkspaceByUid(ws.workspaceUid);
                                }, children: _jsx(ReloadOutlined, { style: { fontSize: 13 } }) }))), _jsx("button", { className: `${style.actionBtn} ${isTesting ? style.spinIcon : ''}`, title: ze('测试连接', 'Test Connection'), onClick: (e) => {
                                    e.stopPropagation();
                                    if (!isTesting)
                                        testWorkspaceByUid(ws.workspaceUid);
                                }, children: _jsx(FiRefreshCw, { size: 13 }) }), _jsx(Dropdown, { overlay: moreActionsMenu, trigger: ['click'], placement: "bottomRight", children: _jsx("button", { className: style.actionBtn, title: ze('更多操作', 'More actions'), onClick: (e) => e.stopPropagation(), children: _jsx(EllipsisOutlined, { style: { fontSize: 16 } }) }) })] }) })] }, ws.workspaceUid));
    }
    // -------------------- Render: Preview content --------------------
    function formatMachineIp(ws) {
        const ip = (ws.machineIp || '').trim();
        if (ip)
            return ip;
        return '';
    }
    /**
     * 卡片"环境 ID"展示值：后端 envName 是按固定规则 `crashsight-claw-${workspaceUid}` 实时推导出来的
     * （不落库，见 CrashClawWorkspaceDto.envName 的 Javadoc），方便用户在 AnyDev 工作台按完整名称找到
     * 对应容器；但卡片上再展示这个公共前缀纯属冗余信息——所有工作区都一样，徒增长度导致被截断，还跟
     * 详情弹窗"工作区 ID"字段（只展示 workspaceUid，不带前缀）看起来不一致。这里只做展示层裁剪；
     * 「AnyDev 部署结果」调试弹窗仍展示完整 envName，不受影响。
     */
    function formatEnvId(ws) {
        const raw = (ws.envName || '').trim();
        if (!raw)
            return '';
        const prefix = 'crashsight-claw-';
        return raw.startsWith(prefix) ? raw.slice(prefix.length) : raw;
    }
    /** 轻量相对时间格式化（卡片"创建人"副行用），项目内暂无现成 util，不引入新依赖 */
    function formatRelativeTime(dateStr) {
        if (!dateStr)
            return '';
        const time = new Date(dateStr).getTime();
        if (Number.isNaN(time))
            return '';
        const diffMs = Date.now() - time;
        if (diffMs < 0)
            return ze('刚刚', 'just now');
        const diffMin = Math.floor(diffMs / 60000);
        if (diffMin < 1)
            return ze('刚刚', 'just now');
        if (diffMin < 60)
            return ze(`${diffMin} 分钟前`, `${diffMin}m ago`);
        const diffHour = Math.floor(diffMin / 60);
        if (diffHour < 24)
            return ze(`${diffHour} 小时前`, `${diffHour}h ago`);
        const diffDay = Math.floor(diffHour / 24);
        if (diffDay < 30)
            return ze(`${diffDay} 天前`, `${diffDay}d ago`);
        const diffMonth = Math.floor(diffDay / 30);
        if (diffMonth < 12)
            return ze(`${diffMonth} 个月前`, `${diffMonth}mo ago`);
        const diffYear = Math.floor(diffMonth / 12);
        return ze(`${diffYear} 年前`, `${diffYear}y ago`);
    }
    function startInlineNameEdit() {
        if (!selectedWorkspace || !canManageWorkspace(selectedWorkspace))
            return;
        setInlineNameDraft(selectedWorkspace.workspaceName || '');
        setInlineNameEditing(true);
    }
    function cancelInlineNameEdit() {
        setInlineNameEditing(false);
        setInlineNameDraft('');
    }
    async function saveInlineName() {
        if (!selectedWorkspace)
            return;
        const nextName = inlineNameDraft.trim();
        if (!nextName) {
            message.warning(ze('名称不能为空', 'Name cannot be empty'));
            return;
        }
        if (nextName === selectedWorkspace.workspaceName) {
            cancelInlineNameEdit();
            return;
        }
        setInlineNameSaving(true);
        try {
            await RestHelper.mustPost(`${API_PREFIX}/updateWorkspace`, {
                workspaceUid: selectedWorkspace.workspaceUid,
                workspaceName: nextName,
                agentUid: selectedWorkspace.agentUid,
                description: selectedWorkspace.description || null,
            });
            // displayName 展示优先级高于 workspaceName；管理者改共享工作区名称时，
            // 本地状态也必须保留“创建者 RTX 前 8 位-”前缀，与后端 listWorkspaces 一致。
            const namePatch = {
                workspaceName: nextName,
                displayName: buildWorkspaceDisplayName(selectedWorkspace, nextName),
            };
            setSelectedWorkspace(prev => (prev ? { ...prev, ...namePatch } : prev));
            setWorkspaces(prev => prev.map(w => (w.workspaceUid === selectedWorkspace.workspaceUid
                ? { ...w, ...namePatch }
                : w)));
            cancelInlineNameEdit();
            notifyCrashClawWorkspacesChanged();
            message.success(ze('名称已更新', 'Name updated'));
        }
        catch (e) {
            console.error(e);
            message.error(ze('更新失败', 'Update failed'));
        }
        finally {
            setInlineNameSaving(false);
        }
    }
    /**
     * 拉取某工作区已创建的认证组 + 仓库绑定，拍平成一个列表（本期不展示分组边界，
     * 见 spec §7.2）。
     */
    async function loadRepoBindings(workspaceUid) {
        repoBindingsRequestWorkspaceUidRef.current = workspaceUid;
        setRepoBindingsLoading(true);
        setRepoBindingsError(false);
        try {
            const rsp = await RestHelper.mustPost(`${API_PREFIX}/listRepoCredentials`, {
                workspaceUid,
            });
            // 响应回来时如果已经不是当前这个工作区在请求了（用户切换到了别的工作区），
            // 丢弃这次结果，不要覆盖当前正在展示的工作区的数据
            if (repoBindingsRequestWorkspaceUidRef.current !== workspaceUid)
                return;
            const flat = [];
            let svnCred = null;
            for (const cred of rsp.json.data || []) {
                // 2026-08-14：捕获 SVN 凭证信息（供追加路径模式预填 + 修改凭证 Modal）
                if (cred.repoType === 'SVN_SELF_HOSTED' && cred.credentialUid) {
                    svnCred = {
                        credentialUid: cred.credentialUid,
                        svnUrl: cred.svnUrl || '',
                        username: cred.username || '',
                    };
                }
                for (const b of cred.bindings || []) {
                    flat.push({
                        repoUid: b.repoUid,
                        credentialUid: cred.credentialUid,
                        repoType: cred.repoType,
                        repoIdentifier: b.repoIdentifier,
                        repoRef: b.repoRef,
                        displayName: b.displayName,
                    });
                }
            }
            setRepoBindings(flat);
            setSvnCredential(svnCred);
        }
        catch (e) {
            console.error('listRepoCredentials failed', e);
            if (repoBindingsRequestWorkspaceUidRef.current !== workspaceUid)
                return;
            setRepoBindingsError(true);
            setRepoBindings([]);
            setSvnCredential(null);
        }
        finally {
            if (repoBindingsRequestWorkspaceUidRef.current === workspaceUid) {
                setRepoBindingsLoading(false);
            }
        }
    }
    async function handleDeleteRepoBinding(repoUid) {
        try {
            await RestHelper.mustPost(`${API_PREFIX}/deleteRepoBinding`, { repoUid });
            setRepoBindings(prev => prev.filter(r => r.repoUid !== repoUid));
            message.success(ze('已删除', 'Deleted'));
        }
        catch (e) {
            console.error('deleteRepoBinding failed', e);
            message.error(ze('删除失败，请稍后重试', 'Delete failed, please try again'));
        }
    }
    // 2026-08-14：修改 SVN 凭证密码（svnUrl/username 已有 binding 时被锁，只让改密码，见方案 A）
    async function handleSaveSvnCredentialPassword() {
        if (!svnCredential)
            return;
        if (!editSvnPassword) {
            message.warning(ze('请输入新密码', 'Please enter the new password'));
            return;
        }
        setSavingSvnCredential(true);
        try {
            // svnUrl/username 原样回传（只读未改），后端在有 binding 时会校验它们与已存值一致
            await RestHelper.mustPost(`${API_PREFIX}/updateSvnCredential`, {
                credentialUid: svnCredential.credentialUid,
                svnUrl: svnCredential.svnUrl,
                username: svnCredential.username,
                password: editSvnPassword,
            });
            message.success(ze('SVN 凭证密码已更新', 'SVN credential password updated'));
            setSvnCredentialEditVisible(false);
            setEditSvnPassword('');
        }
        catch (e) {
            console.error('updateSvnCredential failed', e);
            const backendMessage = e?.message;
            message.error(backendMessage || ze('保存失败，请稍后重试', 'Save failed, please try again'));
        }
        finally {
            setSavingSvnCredential(false);
        }
    }
    // 2026-08-14：删除整个 SVN 凭证（级联删所有 binding），用于"换服务器/账号需先清除"场景
    async function handleDeleteSvnCredential() {
        if (!svnCredential)
            return;
        setDeletingSvnCredential(true);
        try {
            await RestHelper.mustPost(`${API_PREFIX}/deleteRepoCredential`, {
                credentialUid: svnCredential.credentialUid,
            });
            message.success(ze('SVN 授权已删除', 'SVN authorization deleted'));
            setSvnCredentialEditVisible(false);
            setEditSvnPassword('');
            if (selectedWorkspace)
                void loadRepoBindings(selectedWorkspace.workspaceUid);
        }
        catch (e) {
            console.error('deleteRepoCredential failed', e);
            message.error(ze('删除失败，请稍后重试', 'Delete failed, please try again'));
        }
        finally {
            setDeletingSvnCredential(false);
        }
    }
    function repoTypeIcon(repoType) {
        if (repoType === 'GIT')
            return '🔗';
        // 2026-08-13 更新：本期私有化 SVN 的枚举值是 SVN_SELF_HOSTED（区分未来预留的工蜂
        // SVN，见 spec §2.7），不是笼统的 'SVN'
        if (repoType === 'SVN_SELF_HOSTED')
            return '📁';
        // 2026-09-08 新增（TAPD 1020421727137935765）：工蜂 TSVN（工蜂原生 SVN，SSH 公钥
        // 模式），跟 SVN_SELF_HOSTED（私有化部署、用户名密码）是完全不同的类型，图标区分开。
        if (repoType === 'TSVN')
            return '🐢';
        if (repoType === 'P4')
            return '🗄';
        return '📦';
    }
    /**
     * 2026-08-14 新增：按 repoType 分组展示时每组的小标题。跟 repoTypeIcon 配合使用，
     * 让"工蜂 Git 仓库"和"SVN 私有仓库"在详情弹窗里各自独立成区，不再混在同一个
     * "工蜂仓库"标题下（见 spec §7.2 2026-08-14 更新）。
     */
    function repoTypeLabel(repoType) {
        if (repoType === 'GIT')
            return ze('工蜂 Git 仓库', 'Gitee (Git) Repositories');
        if (repoType === 'SVN_SELF_HOSTED')
            return ze('SVN 私有仓库', 'Self-hosted SVN Repositories');
        if (repoType === 'TSVN')
            return ze('工蜂 TSVN 仓库', 'Gitee TSVN Repositories');
        if (repoType === 'P4')
            return ze('P4 仓库', 'P4 Repositories');
        return ze('代码仓库', 'Code Repositories');
    }
    /**
     * 2026-08-14 新增：把扁平的 repoBindings 按 repoType 分组，固定顺序 GIT → SVN_SELF_HOSTED
     * → TSVN → P4 → 其它，每组保留原列表顺序。供详情弹窗"代码仓库"区域分组渲染。
     * 2026-09-08 新增 TSVN（追加在 SVN_SELF_HOSTED 之后、P4 之前，不打乱既有顺序）。
     */
    const repoBindingsByType = useMemo(() => {
        const order = ['GIT', 'SVN_SELF_HOSTED', 'TSVN', 'P4'];
        const groups = new Map();
        for (const rb of repoBindings) {
            const list = groups.get(rb.repoType) || [];
            list.push(rb);
            groups.set(rb.repoType, list);
        }
        const ordered = [];
        for (const t of order) {
            const items = groups.get(t);
            if (items && items.length > 0)
                ordered.push({ repoType: t, items });
            groups.delete(t);
        }
        // 兜底：未来可能出现的新类型，按出现顺序追加
        for (const [t, items] of groups) {
            if (items.length > 0)
                ordered.push({ repoType: t, items });
        }
        return ordered;
    }, [repoBindings]);
    /**
     * 详情弹窗顶部的 Bridge 版本 Alert（见 spec §7.2 A.3）：按优先级只展示一档。
     *   1) 版本未知 / 缺站点所需能力 → warning/error，主按钮「升级 Bridge」（无 capability
     *      则改为「复制安装命令」）+ 次按钮「复制安装命令」
     *   2) 能力齐全但 build 落后 latest → info，「升级 Bridge」+「复制安装命令」
     *   3) 已是最新且能力齐全 → 不展示
     */
    function renderBridgeUpgradeAlert(ws, canManage) {
        const hardIssue = isBridgeVersionUnknown(ws) || isMissingRequiredBridgeCapability(ws);
        const softIssue = !hardIssue && isBridgeOutdated(ws);
        if (!hardIssue && !softIssue)
            return null;
        const canUpgrade = canSelfUpgradeBridge(ws);
        const isUpgrading = upgradingWorkspaceIds.has(ws.workspaceUid);
        const bridgeType = resolveWorkspaceBridgeType(ws);
        const copyInstallCommandButton = (_jsx(Button, { size: "small", onClick: () => {
                if (!ws.bridgeToken) {
                    message.warning(ze('仅工作区所有者可复制安装命令', 'Only the workspace owner can copy the install command'));
                    return;
                }
                // 复用卡片「安装命令」区域可能已填过的 LLM API Key（同一 workspaceUid 的组件级缓存）：
                // 老 Bridge 已装过一次，正常走这里升级用不到 key（install.sh 检测到已有配置会跳过
                // Step 4）；但如果这实际是个从未装过的全新工作区（bridgeBuildVersion 恰好也是
                // unknown），Hermes install.sh 在没有 key 时会在 Step 4 直接报错退出，这里尽量兜底
                // 带上，避免用户复制出一条注定失败的命令。
                navigator.clipboard.writeText(generateBridgeInstallCommand(ws.bridgeToken, bridgeType, getCardLlmApiKey(ws.workspaceUid)));
                message.success(ze('安装命令已复制，请在开发机上执行', 'Install command copied; run it on your dev machine'));
            }, children: ze('复制安装命令', 'Copy install command') }));
        const upgradeButton = canUpgrade ? (_jsx(Button, { size: "small", type: "primary", loading: isUpgrading, disabled: !canManage, title: !canManage ? ze('仅所有者/管理者可升级', 'Only owner/manager can upgrade') : undefined, onClick: () => upgradeBridgeByUid(ws.workspaceUid), children: isUpgrading ? ze('升级中，等待重新连接…', 'Upgrading, waiting for reconnect…') : ze('升级连接程序', 'Upgrade Connector') })) : null;
        return (_jsx(Alert, { style: { marginBottom: 12 }, type: hardIssue ? 'warning' : 'info', showIcon: true, message: hardIssue
                ? ze('本地连接程序版本过旧，部分功能不可用', 'The local connector is outdated; some features are unavailable')
                : ze('有新版本可升级', 'A new version is available'), description: (_jsxs("div", { children: [_jsxs("div", { style: { marginBottom: 8 }, children: [hardIssue
                                ? ze('当前本地连接程序（Bridge）版本未知或缺少该站点所需的能力，请升级后重试。', 'The local connector (Bridge) version is unknown or missing a capability required by this site. Please upgrade and retry.')
                                : ze('当前功能均可正常使用，建议升级到最新版本以获得后续修复与新能力。', 'All features currently work fine; upgrading is recommended to get the latest fixes and capabilities.'), !canUpgrade && (_jsxs("span", { children: [' ', ze('（当前本地连接程序不支持一键升级，请用安装命令手动升一次）', '(one-click upgrade is not supported by this connector; please use the install command instead)')] }))] }), _jsxs("div", { style: { display: 'flex', gap: 8 }, children: [upgradeButton, copyInstallCommandButton] })] })) }));
    }
    function renderPreviewContent() {
        if (!selectedWorkspace)
            return null;
        const ws = selectedWorkspace;
        const owned = isWorkspaceOwner(ws);
        const canManage = canManageWorkspace(ws);
        const displayName = getWorkspaceDisplayName(ws);
        const machineIp = formatMachineIp(ws);
        return (_jsxs("div", { className: style.skillPreview, children: [_jsxs("div", { className: style.skillPreviewNameRow, children: [inlineNameEditing ? (_jsxs("div", { className: style.skillPreviewNameEdit, children: [_jsx(Input, { size: "middle", value: inlineNameDraft, autoFocus: true, disabled: inlineNameSaving, maxLength: 64, onChange: (e) => setInlineNameDraft(e.target.value), onPressEnter: () => { void saveInlineName(); }, onKeyDown: (e) => {
                                        if (e.key === 'Escape')
                                            cancelInlineNameEdit();
                                    } }), _jsx(Button, { type: "text", size: "small", icon: inlineNameSaving ? _jsx(LoadingOutlined, {}) : _jsx(CheckOutlined, {}), disabled: inlineNameSaving, onClick: () => { void saveInlineName(); } }), _jsx(Button, { type: "text", size: "small", icon: _jsx(CloseOutlined, {}), disabled: inlineNameSaving, onClick: cancelInlineNameEdit })] })) : (_jsxs("div", { className: style.skillPreviewNameEdit, children: [_jsx("div", { className: style.skillPreviewName, children: displayName }), canManage && (_jsx(Button, { type: "text", size: "small", icon: _jsx(EditOutlined, {}), title: ze('编辑名称', 'Edit name'), onClick: startInlineNameEdit }))] })), _jsx(Tag, { color: ws.healthStatus === 'HEALTHY' ? 'green' : ws.healthStatus === 'UNHEALTHY' ? 'red' : 'default', children: getHealthText(ws.healthStatus) })] }), !owned && ws.ownerRtx && (_jsx("div", { style: { fontSize: 12, color: '#86909c', marginBottom: 12 }, children: ze(`来自 ${ws.ownerRtx} 的共享工作区`, `Shared workspace from ${ws.ownerRtx}`) })), ws.lastHealthCheckTime && (_jsxs("div", { style: { fontSize: 12, color: '#999', marginBottom: 12 }, children: [ze('上次检测: ', 'Last check: '), new Date(ws.lastHealthCheckTime).toLocaleString()] })), renderBridgeUpgradeAlert(ws, canManage), _jsxs("div", { className: style.skillPreviewSection, children: [_jsx("div", { className: style.skillPreviewLabel, children: ze('基本信息', 'Basic Info') }), _jsxs("div", { className: style.workspaceInfoGrid, children: [_jsxs("div", { className: style.workspaceInfoRow, children: [_jsx("span", { className: style.workspaceInfoLabel, children: ze('名称', 'Name') }), _jsxs("span", { className: style.workspaceInfoValue, children: [inlineNameEditing ? inlineNameDraft.trim() || displayName : displayName, canManage && !inlineNameEditing && (_jsx(Button, { size: "small", type: "text", icon: _jsx(EditOutlined, {}), title: ze('编辑名称', 'Edit name'), onClick: startInlineNameEdit }))] })] }), _jsxs("div", { className: style.workspaceInfoRow, children: [_jsx("span", { className: style.workspaceInfoLabel, children: ze('创建者', 'Creator') }), _jsx("span", { className: style.workspaceInfoValue, children: ws.ownerRtx || ze('—', '—') })] }), _jsxs("div", { className: style.workspaceInfoRow, children: [_jsx("span", { className: style.workspaceInfoLabel, children: ze('创建时间', 'Created') }), _jsx("span", { className: style.workspaceInfoValue, children: ws.createTime ? new Date(ws.createTime).toLocaleString() : ze('—', '—') })] }), _jsxs("div", { className: style.workspaceInfoRow, children: [_jsx("span", { className: style.workspaceInfoLabel, children: ze('工作区 ID', 'Workspace ID') }), _jsxs("span", { className: style.workspaceInfoValue, children: [_jsx("code", { style: { fontSize: 12 }, children: ws.workspaceUid }), _jsx(Button, { size: "small", type: "text", icon: _jsx(CopyOutlined, {}), onClick: () => {
                                                        navigator.clipboard.writeText(ws.workspaceUid || '');
                                                        message.success(ze('已复制', 'Copied'));
                                                    } })] })] }), _jsxs("div", { className: style.workspaceInfoRow, children: [_jsx("span", { className: style.workspaceInfoLabel, children: ze('Claw 类型', 'Claw Type') }), _jsx("span", { className: style.workspaceInfoValue, children: ws.agentType === 'hermes' ? 'Hermes'
                                                : ws.agentType === 'codebuddy' ? 'CodeBuddy'
                                                    : ws.agentType || ze('—', '—') })] }), _jsxs("div", { className: style.workspaceInfoRow, children: [_jsx("span", { className: style.workspaceInfoLabel, title: ze('本地连接程序（Bridge）版本，即安装在你开发机上、负责连接 CrashSight 的程序', 'Version of the local connector program (Bridge) installed on your dev machine that connects it to CrashSight'), children: ze('连接程序版本', 'Connector Version') }), _jsxs("span", { className: style.workspaceInfoValue, children: [ws.bridgeBuildVersion || ze('未知', 'Unknown'), (() => {
                                                    const latest = bridgeLatestInfoCache[resolveWorkspaceBridgeType(ws)];
                                                    if (!latest || !ws.bridgeBuildVersion || latest.buildVersion === ws.bridgeBuildVersion)
                                                        return null;
                                                    return (_jsx("span", { style: { marginLeft: 6, fontSize: 12, color: '#86909c' }, children: ze(`最新：${latest.buildVersion}`, `Latest: ${latest.buildVersion}`) }));
                                                })()] })] }), _jsxs("div", { className: style.workspaceInfoRow, children: [_jsx("span", { className: style.workspaceInfoLabel, children: ze('环境类型', 'Env Type') }), _jsx("span", { className: style.workspaceInfoValue, children: ws.envType === 'anydev' ? 'AnyDev'
                                                : ws.envType === 'cvm' ? ze('CVM / 开发机', 'CVM / Dev Machine')
                                                    : ze('—', '—') })] }), _jsxs("div", { className: style.workspaceInfoRow, children: [_jsx("span", { className: style.workspaceInfoLabel, children: ze('机器 IP', 'Machine IP') }), _jsxs("span", { className: style.workspaceInfoValue, children: [machineIp || ze('—', '—'), machineIp ? (_jsx(Button, { size: "small", type: "text", icon: _jsx(CopyOutlined, {}), onClick: () => {
                                                        navigator.clipboard.writeText(machineIp);
                                                        message.success(ze('已复制', 'Copied'));
                                                    } })) : null] })] }), _jsxs("div", { className: style.workspaceInfoRow, children: [_jsx("span", { className: style.workspaceInfoLabel, children: ze('工作目录', 'Workspace Dir') }), _jsx("span", { className: style.workspaceInfoValue, children: ws.workspaceDir || ze('—', '—') })] }), _jsxs("div", { className: style.workspaceInfoRow, children: [_jsx("span", { className: style.workspaceInfoLabel, children: ze('权限', 'Permission') }), _jsx("span", { className: style.workspaceInfoValue, children: ws.accessRole === 'OWNER' ? ze('所有者', 'Owner')
                                                : ws.accessRole === 'MANAGER' ? ze('管理者', 'Manager')
                                                    : ws.accessRole === 'USER' ? ze('只读', 'User')
                                                        : ze('—', '—') })] })] })] }), _jsxs("div", { className: style.skillPreviewSection, children: [_jsx("div", { className: style.skillPreviewLabel, children: ze('代码仓库', 'Code Repositories') }), repoBindingsLoading ? (_jsx("div", { style: { padding: '12px 0' }, children: _jsx(LoadingOutlined, {}) })) : repoBindingsError ? (_jsxs("div", { className: style.workspaceRepoPlaceholder, children: [_jsx("div", { className: style.workspaceRepoPlaceholderTitle, children: ze('仓库列表加载失败', 'Failed to load repository list') }), _jsx(Button, { size: "small", icon: _jsx(ReloadOutlined, {}), style: { marginTop: 8 }, onClick: () => { if (selectedWorkspace)
                                        void loadRepoBindings(selectedWorkspace.workspaceUid); }, children: ze('重试', 'Retry') })] })) : repoBindings.length === 0 ? (_jsxs("div", { className: style.workspaceRepoPlaceholder, children: [_jsx("div", { className: style.workspaceRepoPlaceholderTitle, children: ze('当前未绑定仓库', 'No repository bound') }), _jsx("div", { className: style.workspaceRepoPlaceholderHint, children: ze('支持 Git、SVN、TSVN 一种或多种', 'Supports Git / SVN / TSVN') })] })) : (
                        /* 2026-08-14：按 repoType 分组，每组带类型专属小标题（工蜂 Git 仓库 / SVN 私有仓库），
                           不再把 Git 和 SVN 混在同一个"工蜂仓库"标题下。§2.6 每类型最多一组认证，
                           但一组下可有多条绑定，所以每组内仍是列表。 */
                        repoBindingsByType.map(group => (_jsxs("div", { className: style.workspaceRepoGroup, children: [_jsxs("div", { className: style.workspaceRepoGroupLabel, children: [_jsx("span", { children: repoTypeIcon(group.repoType) }), _jsx("span", { children: repoTypeLabel(group.repoType) }), owned && group.repoType === 'SVN_SELF_HOSTED' && svnCredential && (_jsx("a", { className: style.workspaceRepoGroupAction, onClick: () => { setEditSvnPassword(''); setSvnCredentialEditVisible(true); }, children: ze('修改凭证', 'Edit credential') }))] }), _jsx("div", { className: style.workspaceRepoList, children: group.items.map(rb => (_jsxs("div", { className: style.workspaceRepoItem, children: [_jsx("span", { className: style.workspaceRepoItemName, children: rb.displayName || rb.repoIdentifier }), rb.repoRef && (_jsx("span", { className: style.workspaceRepoItemRef, children: rb.repoRef })), owned && (_jsx(Button, { type: "text", size: "small", danger: true, icon: _jsx(DeleteOutlined, {}), onClick: () => { void handleDeleteRepoBinding(rb.repoUid); } }))] }, rb.repoUid))) })] }, group.repoType)))), owned && (_jsx(Button, { type: "dashed", icon: _jsx(PlusOutlined, {}), onClick: () => setRepoAuthorizeModalVisible(true), style: { marginTop: 10 }, children: ze('授权仓库  支持 Git、SVN、TSVN 一种或多种', 'Authorize repo (Git / SVN / TSVN)') }))] }), canManage && ws.agentUid && (_jsxs("div", { className: style.skillPreviewSection, children: [_jsx("div", { className: style.skillPreviewLabel, children: ze('LLM 模型', 'LLM Model') }), _jsx(Select, { value: getAgentModelPreference(ws.agentUid, ws.workspaceUid), loading: modelUpdatingWorkspaceUid === ws.workspaceUid, disabled: ws.healthStatus !== 'HEALTHY' || modelUpdatingWorkspaceUid === ws.workspaceUid, options: llmModels.map(m => ({ value: m.value, label: m.label })), onChange: (value) => handleWorkspaceModelChange(ws.workspaceUid, ws.agentUid, value), style: { width: '100%' }, placeholder: ze('请选择模型', 'Select a model') }), _jsx("div", { style: { fontSize: 11, color: '#999', marginTop: 4 }, children: ze('修改后将自动下发配置并重启 Agent', 'Changes are synced and Agent restarts automatically') })] })), owned && ws.bridgeToken && (_jsxs("div", { className: style.skillPreviewSection, children: [_jsx("div", { className: style.skillPreviewLabel, children: ze('Bridge Token', 'Bridge Token') }), _jsxs("div", { className: style.workspaceBridgeTokenRow, children: [_jsx("code", { className: style.workspaceBridgeTokenValue, children: ws.bridgeToken }), _jsx(Button, { size: "small", onClick: () => {
                                        navigator.clipboard.writeText(ws.bridgeToken || '');
                                        message.success(ze('已复制', 'Copied'));
                                    }, children: ze('复制', 'Copy') })] }), _jsx("div", { style: { fontSize: 11, color: '#999', marginTop: 4 }, children: ze('用于 Bridge 程序连接认证，请妥善保管', 'Used for Bridge authentication, keep it safe') }), ws.envType !== 'anydev' && ws.healthStatus !== 'DEPLOYING' && ws.healthStatus !== 'FAILED' && (_jsxs("div", { className: style.bridgeCmdSection, children: [_jsx("div", { className: style.bridgeCmdLabel, children: ze('安装命令', 'Install Command') }), SHOW_CODEBUDDY_BRIDGE_OPTION && (_jsx("div", { style: { marginBottom: 8 }, children: _jsxs(Radio.Group, { size: "small", value: getCardBridgeType(ws.workspaceUid), onChange: (e) => setCardBridgeType(ws.workspaceUid, e.target.value), children: [_jsx(Radio.Button, { value: "hermes", children: ze('Hermes（推荐）', 'Hermes (Recommended)') }), _jsx(Radio.Button, { value: "codebuddy", children: ze('CodeBuddy', 'CodeBuddy') })] }) })), (!SHOW_CODEBUDDY_BRIDGE_OPTION || getCardBridgeType(ws.workspaceUid) === 'hermes') && (_jsxs("div", { style: { marginBottom: 8 }, children: [_jsxs("div", { style: { fontSize: 12, color: '#4e5969', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }, children: [_jsx("span", { children: ze('LLM API Key (Venus)', 'LLM API Key (Venus)') }), _jsx("a", { href: VENUS_PERSONAL_ACCOUNT_URL, target: "_blank", rel: "noopener noreferrer", style: { fontSize: 12 }, children: ze('前往获取 →', 'Get key →') })] }), _jsx(CsFakePasswordInput, { size: "small", placeholder: ze('粘贴你的 Venus secretKey（形如 xxxxxxxx@1234）', 'Paste your Venus secretKey (e.g. xxxxxxxx@1234)'), value: getCardLlmApiKey(ws.workspaceUid), onChange: (e) => setCardLlmApiKey(ws.workspaceUid, e.target.value), onClick: (e) => e.stopPropagation(), autoComplete: "off", name: "llm-secret-nonpwd" }), _jsx("div", { style: { fontSize: 11, color: '#999', marginTop: 4 }, children: ze('不会上传到 CrashSight 服务器，仅作为 install.sh 的 --llm-api-key 参数写入你本地 / AnyDev 容器内的 Hermes 配置。', 'Not uploaded to CrashSight servers; used only as the --llm-api-key argument written into the Hermes config on your local machine / AnyDev container.') })] })), SHOW_CODEBUDDY_BRIDGE_OPTION && getCardBridgeType(ws.workspaceUid) === 'codebuddy' && (_jsxs("div", { style: { marginBottom: 8 }, children: [_jsxs("div", { style: { fontSize: 12, color: '#4e5969', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }, children: [_jsx("span", { children: ze('CodeBuddy API Key', 'CodeBuddy API Key') }), _jsx("a", { href: CODEBUDDY_API_KEY_URL, target: "_blank", rel: "noopener noreferrer", style: { fontSize: 12 }, children: ze('前往获取 →', 'Get key →') })] }), _jsx(CsFakePasswordInput, { size: "small", placeholder: ze('粘贴你的 CodeBuddy API Key', 'Paste your CodeBuddy API Key'), value: getCardLlmApiKey(ws.workspaceUid), onChange: (e) => setCardLlmApiKey(ws.workspaceUid, e.target.value), onClick: (e) => e.stopPropagation(), autoComplete: "off", name: "codebuddy-secret-nonpwd" }), _jsx("div", { style: { fontSize: 11, color: '#999', marginTop: 4 }, children: ze('不会上传到 CrashSight 服务器，仅作为 install.sh 的 --codebuddy-api-key 参数写入 AnyDev 容器环境变量。', 'Not uploaded to CrashSight servers; used only as the --codebuddy-api-key argument written into the container environment variable.') })] })), _jsx("pre", { className: style.bridgeCmdBlock, children: generateBridgeInstallCommand(ws.bridgeToken, getEffectiveBridgeType(getCardBridgeType(ws.workspaceUid)), getCardLlmApiKey(ws.workspaceUid)) }), _jsx(Button, { size: "small", icon: _jsx(CopyOutlined, {}), onClick: () => {
                                        navigator.clipboard.writeText(generateBridgeInstallCommand(ws.bridgeToken, getEffectiveBridgeType(getCardBridgeType(ws.workspaceUid)), getCardLlmApiKey(ws.workspaceUid)));
                                        message.success(ze('已复制到剪贴板', 'Copied to clipboard'));
                                    }, children: ze('复制命令', 'Copy Command') }), _jsxs("div", { style: { marginTop: 8, paddingTop: 8, borderTop: '1px dashed #e5e6eb' }, children: [_jsx("div", { style: { fontSize: 12, color: '#86909c', marginBottom: 6 }, children: ze('没有开发机？', 'No development machine?') }), _jsx(Button, { size: "small", type: "link", icon: _jsx(ThunderboltOutlined, {}), loading: anydevDeploying, onClick: (e) => {
                                                e.stopPropagation(); // 防止点按钮误触发卡片的 openPreviewModal
                                                handleDeployToAnydev(ws.workspaceUid, getEffectiveBridgeType(getCardBridgeType(ws.workspaceUid)), getCardLlmApiKey(ws.workspaceUid));
                                            }, style: { padding: 0 }, children: ze('一键创建 AnyDev 开发容器并自动部署', 'Auto-create AnyDev container & deploy') })] })] }))] })), ws.description && (_jsxs("div", { className: style.skillPreviewSection, children: [_jsx("div", { className: style.skillPreviewLabel, children: ze('描述', 'Description') }), _jsx("div", { className: style.skillPreviewValue, children: ws.description })] }))] }));
    }
    // -------------------- Render: Form content --------------------
    /**
     * 编辑模式下沿用旧表单（不展示环境选择卡片，因为环境类型创建后不可改）。
     * 创建模式下显示环境卡片 + 按类型分支的表单。
     */
    function renderFormContent() {
        if (modalMode === 'edit') {
            return renderClassicForm(false);
        }
        return renderCreateForm();
    }
    /**
     * CVM/开发机 Tab：填写名称/描述，点「创建工作区」后弹出 Bridge 安装命令。
     */
    function renderCvmForm() {
        return (_jsxs(_Fragment, { children: [renderClassicForm(false), _jsxs("div", { className: style.bridgeCmdModalHint, style: { marginTop: 8 }, children: [_jsx(Tag, { color: "orange", style: { marginRight: 8 }, children: ze('仅支持 Mac / Linux', 'Mac / Linux only') }), ze('创建成功后将展示 Bridge 安装命令。请在你的 Mac 或 Linux 开发机上执行 install.sh，Bridge 连上后工作区状态会变绿。', 'After creation the Bridge install command will be shown. Run install.sh on your Mac or Linux dev machine; the workspace will turn online once Bridge connects.')] }), _jsxs("div", { style: {
                        marginTop: 12,
                        padding: '10px 14px',
                        background: '#FFF7E6',
                        border: '1px solid #FFD591',
                        borderRadius: 6,
                        fontSize: 12,
                        color: '#AD6800',
                        lineHeight: 1.6,
                    }, children: [_jsx("span", { style: { fontWeight: 600 }, children: ze('⚠ 安全提示：', '⚠ Security Notice:') }), ze('CVM 形式接入 Hermes 可能触发公司安全策略拦截。推荐使用 CodeBuddy 类型，或联系安全团队申请加白。', 'CVM-based Hermes may trigger company security policies. We recommend using CodeBuddy instead, or contact the security team for whitelisting.')] })] }));
    }
    /**
     * 创建模式：环境卡片 + 按类型分支的表单。
     */
    function renderCreateForm() {
        return (_jsxs("div", { children: [_jsxs("div", { className: style.envCardRow, children: [renderEnvCard('anydev', _jsx(CloudServerOutlined, {}), ze('AnyDev 容器', 'AnyDev Container'), ze('默认 / 推荐', 'Default / Recommended'), ze('系统自动创建云端容器并部署 Hermes Bridge', 'Auto-create cloud container and deploy Hermes Bridge')), renderEnvCard('cvm', _jsx(CodeOutlined, {}), ze('CVM / 开发机', 'CVM / Dev Machine'), ze('仅 Mac / Linux', 'Mac / Linux only'), ze('在你自己的 CVM 或开发机上执行 install.sh 一键安装脚本', 'Run the install.sh script on your own CVM or dev machine'))] }), _jsxs("div", { className: style.createFormSubArea, children: [_jsx("div", { className: createEnvType === 'anydev' ? style.createFormSubPanelActive : style.createFormSubPanelHidden, children: renderAnydevForm() }), _jsx("div", { className: createEnvType === 'cvm' ? style.createFormSubPanelActive : style.createFormSubPanelHidden, children: renderCvmForm() })] })] }));
    }
    function renderEnvCard(key, icon, title, badge, desc) {
        const active = createEnvType === key;
        return (_jsxs("div", { className: `${style.envCard} ${active ? style.envCardActive : ''}`, onClick: () => {
                setCreateEnvType(key);
                // 切换环境时清掉另一套表单里的残留值，避免隐藏字段参与校验
                if (key === 'cvm') {
                    form.setFieldsValue({ llmApiKey: undefined, llmBaseUrl: DEFAULT_LLM_BASE_URL });
                }
            }, children: [_jsx("div", { className: style.envCardIcon, children: icon }), _jsx("div", { className: style.envCardTitle, children: title }), _jsx(Tag, { color: key === 'anydev' ? 'orange' : 'blue', className: style.envCardBadge, children: badge }), _jsx("div", { className: style.envCardDesc, children: desc })] }, key));
    }
    function renderAnydevForm() {
        return (_jsxs(Form, { form: form, layout: "vertical", children: [_jsx(Form.Item, { name: "workspaceName", label: ze('工作区名称', 'Workspace Name'), rules: [{ required: true, message: ze('请输入工作区名称', 'Please enter workspace name') }], children: _jsx(Input, { placeholder: ze('请输入工作区名称，仅支持字母、数字、下划线、中划线', 'Letters, numbers, underscore and hyphen only'), autoComplete: "off" }) }), _jsx(Form.Item, { name: "description", label: ze('描述', 'Description'), children: _jsx(Input.TextArea, { rows: 2, placeholder: ze('备注信息（可选）', 'Notes (optional)') }) }), _jsx(Form.Item, { name: "agentType", label: ze('Agent 类型', 'Agent Type'), initialValue: "hermes", extra: ze('Hermes 是成熟的 AI 分析引擎；CodeBuddy 是腾讯云 CodeBuddy Agent。两者功能等价，任选其一即可。', 'Hermes is a mature AI analysis engine; CodeBuddy is Tencent Cloud CodeBuddy Agent. Both are functionally equivalent.'), children: _jsxs(Radio.Group, { children: [_jsx(Radio.Button, { value: "hermes", children: ze('Hermes（推荐）', 'Hermes (Recommended)') }), _jsx(Radio.Button, { value: "codebuddy", children: ze('CodeBuddy', 'CodeBuddy') })] }) }), _jsx(Form.Item, { name: "llmApiKey", label: (_jsxs("span", { children: [ze('LLM API Key (Venus)', 'LLM API Key (Venus)'), ' ', _jsx("a", { href: VENUS_PERSONAL_ACCOUNT_URL, target: "_blank", rel: "noopener noreferrer", style: { fontSize: 12 }, children: ze('前往获取 →', 'Get key →') })] })), rules: [{ required: true, message: ze('请填写 Venus secretKey', 'Please enter Venus secretKey') }], extra: ze('用于 Hermes LLM Provider 初始化，仅下发到 AnyDev 容器 install.sh，不落库。', 'Used for Hermes LLM Provider init; sent to AnyDev install.sh only, not stored in DB.'), children: _jsx(CsFakePasswordInput, { placeholder: ze('粘贴你的 Venus secretKey（形如 xxxxxxxx@1234）', 'Paste your Venus secretKey'), autoComplete: "off", name: "llm-secret-nonpwd" }) }), _jsx(Form.Item, { name: "llmBaseUrl", hidden: true, initialValue: DEFAULT_LLM_BASE_URL, children: _jsx(Input, {}) }), anydevCreating && (_jsxs("div", { className: style.anydevCreatingHint, children: [_jsx(LoadingOutlined, {}), _jsx("span", { style: { marginLeft: 8 }, children: anydevCreateStep })] }))] }));
    }
    /**
     * 传统创建/编辑表单（CVM / 编辑模式 共用）。
     * 配置流程简化后所有工作区都由后台自动绑定默认 Agent，前端不再暴露 agentUid 字段。
     */
    function renderClassicForm(_requireAgent) {
        return (_jsxs(Form, { form: form, layout: "vertical", children: [_jsx(Form.Item, { name: "workspaceName", label: ze('工作区名称', 'Workspace Name'), rules: [{ required: true, message: ze('请输入工作区名称', 'Please enter workspace name') }], children: _jsx(Input, { placeholder: ze('请输入工作区名称，仅支持字母、数字、下划线、中划线', 'Letters, numbers, underscore and hyphen only'), autoComplete: "off" }) }), _jsx(Form.Item, { name: "description", label: ze('描述', 'Description'), children: _jsx(Input.TextArea, { rows: 2, placeholder: ze('备注信息（可选）', 'Notes (optional)') }) }), _jsx(Form.Item, { name: "agentType", label: ze('Agent 类型', 'Agent Type'), initialValue: "hermes", extra: ze('Hermes 是成熟的 AI 分析引擎；CodeBuddy 是腾讯云 CodeBuddy Agent。两者功能等价，任选其一即可。', 'Hermes is a mature AI analysis engine; CodeBuddy is Tencent Cloud CodeBuddy Agent. Both are functionally equivalent.'), children: _jsxs(Radio.Group, { children: [_jsx(Radio.Button, { value: "hermes", children: ze('Hermes（推荐）', 'Hermes (Recommended)') }), _jsx(Radio.Button, { value: "codebuddy", children: ze('CodeBuddy', 'CodeBuddy') })] }) })] }));
    }
    // -------------------- Render: Log viewer --------------------
    function renderLogModalContent() {
        if (!logModalWorkspace)
            return null;
        const displayName = getWorkspaceDisplayName(logModalWorkspace);
        const selectedOption = WORKSPACE_LOG_OPTIONS.find(o => o.value === logModalKey);
        return (_jsxs("div", { className: style.logViewer, children: [_jsx("div", { className: style.logViewerWorkspace, children: displayName }), _jsxs("div", { className: style.logViewerToolbar, children: [_jsxs("div", { className: style.logViewerField, children: [_jsx("span", { className: style.logViewerLabel, children: ze('日志文件', 'Log file') }), _jsx(Select, { value: logModalKey, style: { minWidth: 220 }, onChange: handleLogKeyChange, options: WORKSPACE_LOG_OPTIONS.map((opt) => ({
                                        value: opt.value,
                                        label: ze(opt.labelZh, opt.labelEn),
                                    })) })] }), _jsxs("div", { className: style.logViewerField, children: [_jsx("span", { className: style.logViewerLabel, children: ze('行数', 'Lines') }), _jsx(InputNumber, { min: 1, max: 2000, value: logModalLines, onChange: (v) => setLogModalLines(typeof v === 'number' ? v : DEFAULT_LOG_TAIL_LINES), onPressEnter: handleRefreshLog })] }), _jsx(Button, { icon: _jsx(ReloadOutlined, {}), loading: logModalLoading, onClick: handleRefreshLog, children: ze('刷新', 'Refresh') })] }), _jsxs("div", { className: style.logViewerMeta, children: [ze('路径', 'Path'), ": ", selectedOption?.path || logModalMeta?.path || '-', logModalMeta && (_jsxs(_Fragment, { children: [' · ', logModalMeta.exists
                                    ? ze(`已返回 ${logModalMeta.returnedLines} 行`, `${logModalMeta.returnedLines} line(s)`)
                                    : ze('文件不存在', 'File not found'), logModalMeta.truncated && (_jsxs(_Fragment, { children: [' · ', ze('大文件已截断扫描', 'Large file scan truncated')] }))] }))] }), _jsxs("div", { className: style.logViewerScrollHost, style: { height: LOG_VIEWER_SCROLL_HEIGHT, minHeight: LOG_VIEWER_SCROLL_HEIGHT }, children: [logModalLoading && (_jsxs("div", { className: style.logViewerLoading, children: [_jsx(LoadingOutlined, {}), " ", ze('加载中…', 'Loading…')] })), _jsx("div", { ref: logScrollRef, className: style.logViewerScroll, "aria-busy": logModalLoading, children: logDisplayLines.length === 0 ? (_jsx("div", { className: style.logViewerEmpty, children: ze('（暂无内容）', '(empty)') })) : (logDisplayLines.map((line, idx) => (_jsxs("div", { className: style.logViewerLine, children: [_jsx("span", { className: style.logViewerLineNo, children: idx + 1 }), _jsx("span", { className: style.logViewerLineText, children: line || ' ' })] }, `${logModalKey}-${idx}`)))) })] })] }));
    }
    // -------------------- Render: Modal chrome --------------------
    function getModalTitle() {
        if (modalMode === 'create')
            return ze('创建工作区', 'Create Workspace');
        if (modalMode === 'edit')
            return ze('编辑工作区', 'Edit Workspace');
        return ze('工作区详情', 'Workspace Details');
    }
    function getModalFooter() {
        if (modalMode === 'preview') {
            const previewRestarting = selectedWorkspace
                ? restartingWorkspaceIds.has(selectedWorkspace.workspaceUid)
                : false;
            const previewOnline = selectedWorkspace?.healthStatus === 'HEALTHY';
            const previewOwned = selectedWorkspace ? isWorkspaceOwner(selectedWorkspace) : false;
            const previewCanManage = selectedWorkspace ? canManageWorkspace(selectedWorkspace) : false;
            return (_jsxs("div", { className: style.modalFooter, children: [previewOwned ? (_jsx(Button, { danger: true, onClick: () => handleDeleteWorkspace(), children: ze('删除', 'Delete') })) : (_jsx("span", {})), _jsxs("div", { className: style.modalFooterRight, children: [previewCanManage && (_jsx(Button, { icon: _jsx(ReloadOutlined, {}), loading: previewRestarting, disabled: !previewOnline || previewRestarting, onClick: () => {
                                    if (selectedWorkspace)
                                        restartWorkspaceByUid(selectedWorkspace.workspaceUid);
                                }, children: ze('重启 Agent', 'Restart Agent') })), previewCanManage && (_jsx(Button, { type: "primary", onClick: switchToEditMode, children: ze('编辑', 'Edit') }))] })] }));
        }
        // AnyDev / CVM 创建 / 编辑：表单提交
        const isAnydev = modalMode === 'create' && createEnvType === 'anydev';
        const isCvmCreate = modalMode === 'create' && createEnvType === 'cvm';
        const okLoading = modalMode === 'create' && isAnydev ? anydevCreating : submitting;
        const okText = isAnydev
            ? ze('一键创建', 'Quick Create')
            : isCvmCreate
                ? ze('创建工作区', 'Create Workspace')
                : ze('确定', 'OK');
        return (_jsxs("div", { className: style.modalFooterRight, children: [_jsx(Button, { onClick: closeModal, disabled: okLoading, children: ze('取消', 'Cancel') }), _jsx(Button, { type: "primary", onClick: modalMode === 'create' ? handleCreateSubmit : handleEditSubmit, loading: okLoading, children: okText })] }));
    }
    // -------------------- Main render --------------------
    const hasWorkspaces = workspaces.length > 0;
    return (_jsxs("div", { className: style.sectionBody, children: [hasWorkspaces && (_jsxs("div", { className: style.agentOpBar, children: [_jsx("div", { className: style.agentCount, children: ze(`共 ${workspaces.length} 个工作区`, `${workspaces.length} Workspace(s)`) }), _jsx(Button, { type: "primary", icon: _jsx(PlusOutlined, {}), onClick: openCreateModal, children: ze('创建工作区', 'Create Workspace') })] })), loading ? (_jsx("div", { className: style.skillsEmpty, children: ze('加载中…', 'Loading…') })) : !hasWorkspaces ? (_jsxs("div", { className: style.quickSetupEmpty, children: [_jsx("div", { className: style.quickSetupIcon, children: _jsx(RocketOutlined, {}) }), _jsx("div", { className: style.quickSetupTitle, children: ze('你当前还没有工作区', 'You don\'t have any workspaces yet') }), _jsx("div", { className: style.quickSetupDesc, children: ze('点击下方按钮，选择部署环境后即可创建。AnyDev 模式将自动为你配置 CrashSight MCP 和默认 Agent，无需 API Key。', 'Click the button below and pick a deployment environment. AnyDev mode auto-configures CrashSight MCP and a default Agent, no API Key required.') }), _jsx(Button, { type: "primary", size: "large", icon: _jsx(ThunderboltOutlined, {}), loading: quickCreating, onClick: openCreateModal, className: style.quickSetupBtn, children: ze('创建第一个工作区', 'Create your first workspace') })] })) : (_jsx("div", { className: style.skillsGrid, children: workspaces.map(ws => renderWorkspaceCard(ws)) })), _jsx(WorkspaceSharingModal, { visible: sharingModalVisible, workspaceUid: sharingTarget?.uid || '', workspaceName: sharingTarget?.name || '', creatorRtx: sharingTarget?.creatorRtx, mode: "edit", onClose: async () => {
                    const uid = sharingTarget?.uid;
                    setSharingModalVisible(false);
                    setSharingTarget(null);
                    // 关闭后单独刷新这条 ws 的共享范围摘要（避免重拉全部）
                    if (uid) {
                        const scope = await fetchWorkspaceSharingScope(uid);
                        setSharingScopeMap(prev => ({ ...prev, [uid]: scope }));
                    }
                } }), _jsx(RepoAuthorizeModal, { visible: repoAuthorizeModalVisible, workspaceUid: selectedWorkspace?.workspaceUid || '', bridgeOnline: selectedWorkspace?.healthStatus === 'HEALTHY', boundRepoIdentifiers: repoBindings.map(rb => rb.repoIdentifier), 
                // 2026-08-13 晚：bridgeCapabilities 缺 repo_svn_test（含老 Bridge 完全未上报）
                // → SVN 卡片灰置并提示升级。这跟下面 svnCliKnownMissing 不是同一类判断：
                // 前者是"Bridge 程序代码认不认识 SVN 协议"，后者是"宿主机装没装 svn 命令行"。
                // 缺程序能力时优先展示升级文案；hostCapabilities 未上报时不把"不知道装没装"
                // 误判成"确定没装"，见 isHostCapabilityKnownMissing 注释。
                svnCapabilityMissing: !!selectedWorkspace && !hasBridgeCapability(selectedWorkspace, CAP_REPO_SVN_TEST), svnCliKnownMissing: isHostCapabilityKnownMissing(selectedWorkspace, HOST_CAP_SVN_CLI), 
                // 2026-08-14：已有 SVN 凭证时，授权弹窗选 SVN 进入"追加路径"模式（复用已存凭证）
                existingSvnCredential: svnCredential, 
                // 2026-08-14 新增：用户在「SVN 环境未就绪」步骤点「重新检测」成功后，静默刷新
                // 工作区列表，使 svnCliKnownMissing 跟 DB 最新 hostCapabilities 对齐。
                onHostCapabilitiesRefreshed: () => { void fetchData(true); }, onClose: () => setRepoAuthorizeModalVisible(false), onSaved: () => {
                    if (selectedWorkspace)
                        void loadRepoBindings(selectedWorkspace.workspaceUid);
                } }), _jsx(Modal, { title: (_jsxs("span", { children: [_jsx(FolderOutlined, { style: { marginRight: 8 } }), ze('修改 SVN 凭证', 'Edit SVN Credential')] })), open: svnCredentialEditVisible, onCancel: () => { setSvnCredentialEditVisible(false); setEditSvnPassword(''); }, maskClosable: false, zIndex: CRASHCLAW_NESTED_MODAL_Z_INDEX, footer: [
                    _jsx(Button, { danger: true, loading: deletingSvnCredential, onClick: () => { void handleDeleteSvnCredential(); }, children: ze('删除整个 SVN 授权', 'Delete entire SVN authorization') }, "delete"),
                    _jsx(Button, { onClick: () => { setSvnCredentialEditVisible(false); setEditSvnPassword(''); }, children: ze('取消', 'Cancel') }, "cancel"),
                    _jsx(Button, { type: "primary", loading: savingSvnCredential, onClick: () => { void handleSaveSvnCredentialPassword(); }, children: ze('保存新密码', 'Save new password') }, "save"),
                ], children: svnCredential && (_jsxs("div", { className: style.svnFormFieldGroup, children: [_jsxs("div", { className: style.svnCredEditReadonlyRow, children: [_jsx("span", { className: style.svnCredEditReadonlyLabel, children: ze('服务器 URL', 'Server URL') }), _jsx("code", { className: style.svnCredEditReadonlyValue, children: svnCredential.svnUrl })] }), _jsxs("div", { className: style.svnCredEditReadonlyRow, children: [_jsx("span", { className: style.svnCredEditReadonlyLabel, children: ze('用户名', 'Username') }), _jsx("code", { className: style.svnCredEditReadonlyValue, children: svnCredential.username })] }), _jsx("div", { className: style.svnCredEditHint, children: ze('已有授权路径时不能修改服务器地址或用户名。如需更换，请先点下方"删除整个 SVN 授权"后重新配置。', 'Server URL and username cannot be changed while authorized paths exist. To switch server/account, click "Delete entire SVN authorization" below and reconfigure.') }), _jsx(CsFakePasswordInput, { placeholder: ze('输入新密码（留空=不修改）', 'Enter new password (leave empty to keep)'), value: editSvnPassword, onChange: (e) => setEditSvnPassword(e.target.value) })] })) }), _jsx(Modal, { title: (_jsxs("span", { children: [_jsx(BookOutlined, { style: { color: '#1890ff', marginRight: 8 } }), ze('工作区日志', 'Workspace Logs')] })), className: style.logViewerModal, open: logModalVisible, onCancel: closeLogModal, width: 800, destroyOnClose: true, bodyStyle: { paddingTop: 16, paddingBottom: 16 }, footer: (_jsx(Button, { onClick: closeLogModal, children: ze('关闭', 'Close') })), children: renderLogModalContent() }), _jsxs(Modal, { title: getModalTitle(), open: modalVisible, onCancel: closeModal, maskClosable: modalMode === 'preview', width: modalMode === 'create' ? 720 : 560, destroyOnClose: true, 
                // 显式声明 zIndex，保证跟 RepoAuthorizeModal 等嵌套弹窗的层级顺序稳定
                zIndex: CRASHCLAW_MODAL_Z_INDEX, footer: getModalFooter(), children: [modalMode === 'preview' && renderPreviewContent(), (modalMode === 'create' || modalMode === 'edit') && renderFormContent()] }), _jsx(Modal, { title: _jsxs("span", { children: [_jsx(RocketOutlined, { style: { color: '#52c41a', marginRight: 8 } }), ze('工作区创建成功', 'Workspace Created Successfully')] }), open: bridgeCmdVisible, onCancel: () => setBridgeCmdVisible(false), width: 640, footer: _jsxs("div", { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 }, children: [_jsx(Button, { icon: _jsx(CopyOutlined, {}), onClick: handleCopyBridgeCmd, children: ze('复制命令', 'Copy Command') }), _jsx(Button, { type: "primary", onClick: () => setBridgeCmdVisible(false), children: ze('知道了', 'Got it') })] }), children: _jsxs("div", { className: style.bridgeCmdModalBody, children: [_jsx("div", { className: style.bridgeCmdModalHint, children: ze('请在你的开发机上执行以下命令，安装 Hermes Bridge 并自动上线工作区：', 'Run the following command on your development machine to install the Hermes Bridge and bring the workspace online:') }), SHOW_CODEBUDDY_BRIDGE_OPTION && (_jsx("div", { style: { marginBottom: 8 }, children: _jsxs(Radio.Group, { size: "small", value: modalBridgeType, onChange: (e) => {
                                    // 切换 Bridge 类型时清空 key 输入框（同 setCardBridgeType 注释）
                                    setModalBridgeType(e.target.value);
                                    setModalLlmApiKey('');
                                }, children: [_jsx(Radio.Button, { value: "hermes", children: ze('Hermes（推荐）', 'Hermes (Recommended)') }), _jsx(Radio.Button, { value: "codebuddy", children: ze('CodeBuddy', 'CodeBuddy') })] }) })), (!SHOW_CODEBUDDY_BRIDGE_OPTION || modalBridgeType === 'hermes') && (_jsxs("div", { style: { marginBottom: 12 }, children: [_jsxs("div", { style: { fontSize: 13, color: '#1d2129', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }, children: [_jsx("span", { children: ze('LLM API Key (Venus)', 'LLM API Key (Venus)') }), _jsx("a", { href: VENUS_PERSONAL_ACCOUNT_URL, target: "_blank", rel: "noopener noreferrer", style: { fontSize: 12 }, children: ze('前往 Venus 个人账号页面获取 →', 'Get key from Venus personal account →') })] }), _jsx(CsFakePasswordInput, { placeholder: ze('粘贴你的 Venus secretKey（形如 xxxxxxxx@1234）', 'Paste your Venus secretKey (e.g. xxxxxxxx@1234)'), value: modalLlmApiKey, onChange: (e) => setModalLlmApiKey(e.target.value), autoComplete: "off", name: "llm-secret-nonpwd" }), _jsx("div", { style: { fontSize: 11, color: '#86909c', marginTop: 4 }, children: ze('不会上传到 CrashSight 服务器，仅作为 install.sh 的 --llm-api-key 参数写入你本地 / AnyDev 容器内的 Hermes 配置。', 'Not uploaded to CrashSight servers; used only as the --llm-api-key argument written into the Hermes config on your local machine / AnyDev container.') })] })), SHOW_CODEBUDDY_BRIDGE_OPTION && modalBridgeType === 'codebuddy' && (_jsxs("div", { style: { marginBottom: 12 }, children: [_jsxs("div", { style: { fontSize: 13, color: '#1d2129', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }, children: [_jsx("span", { children: ze('CodeBuddy API Key', 'CodeBuddy API Key') }), _jsx("a", { href: CODEBUDDY_API_KEY_URL, target: "_blank", rel: "noopener noreferrer", style: { fontSize: 12 }, children: ze('前往获取 →', 'Get key →') })] }), _jsx(CsFakePasswordInput, { placeholder: ze('粘贴你的 CodeBuddy API Key', 'Paste your CodeBuddy API Key'), value: modalLlmApiKey, onChange: (e) => setModalLlmApiKey(e.target.value), autoComplete: "off", name: "codebuddy-secret-nonpwd" }), _jsx("div", { style: { fontSize: 11, color: '#86909c', marginTop: 4 }, children: ze('不会上传到 CrashSight 服务器，仅作为 install.sh 的 --codebuddy-api-key 参数写入 AnyDev 容器环境变量。', 'Not uploaded to CrashSight servers; used only as the --codebuddy-api-key argument written into the container environment variable.') })] })), _jsx("pre", { className: style.bridgeCmdBlock, children: modalBridgeToken && generateBridgeInstallCommand(modalBridgeToken, getEffectiveBridgeType(modalBridgeType), modalLlmApiKey) }), modalWorkspaceUid && (_jsxs("div", { style: {
                                marginTop: 12,
                                paddingTop: 12,
                                borderTop: '1px dashed #e5e6eb',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }, children: [_jsx("div", { style: { fontSize: 13, color: '#4e5969' }, children: ze('没有开发机？', 'No development machine?') }), _jsx(Button, { type: "primary", ghost: true, icon: _jsx(ThunderboltOutlined, {}), loading: anydevDeploying, onClick: () => handleDeployToAnydev(modalWorkspaceUid, getEffectiveBridgeType(modalBridgeType), modalLlmApiKey), children: ze('一键创建 AnyDev 开发容器并自动部署', 'Auto-create AnyDev container & deploy') })] }))] }) }), _jsx(Modal, { title: _jsxs("span", { children: [_jsx(ThunderboltOutlined, { style: {
                                color: anydevResultData?.success ? '#52c41a' : '#ff4d4f',
                                marginRight: 8,
                            } }), ze('AnyDev 部署结果', 'AnyDev Deploy Result')] }), open: anydevResultVisible, onCancel: () => setAnydevResultVisible(false), width: 720, footer: _jsx(Button, { type: "primary", onClick: () => setAnydevResultVisible(false), children: ze('知道了', 'Got it') }), children: anydevResultData && (_jsxs("div", { className: style.bridgeCmdModalBody, children: [_jsx("div", { className: style.bridgeCmdModalHint, children: anydevResultData.success
                                ? ze('✅ AnyDev 接口调用成功（HTTP 2xx）。容器创建是异步的：通常 1~3 分钟后容器拉起，Bridge 自动启动并连回 CrashSight，Workspace 状态会自动变绿。若长时间未生效，请打开下方的 AnyDev 原始响应排查。', '✅ AnyDev API call succeeded (HTTP 2xx). Container creation is async: it usually takes 1~3 minutes for the container to boot and the Bridge to connect back. If the Workspace stays offline, check the raw response below.')
                                : ze(`❌ AnyDev 接口返回 HTTP ${anydevResultData.statusCode}，部署提交失败。请把下方原始响应反馈给 CrashSight 开发同学排查。`, `❌ AnyDev returned HTTP ${anydevResultData.statusCode}, submission failed. Please share the raw response below with the CrashSight team.`) }), _jsxs("div", { style: { marginTop: 12, fontSize: 13 }, children: [_jsxs("div", { style: { marginBottom: 4 }, children: [_jsxs("strong", { children: [ze('容器名称', 'Container Name'), "\uFF1A"] }), _jsx("code", { children: anydevResultData.envName })] }), _jsxs("div", { style: { marginBottom: 4 }, children: [_jsxs("strong", { children: ["HTTP ", ze('状态码', 'Status'), "\uFF1A"] }), _jsx("code", { children: anydevResultData.statusCode })] })] }), _jsxs("div", { style: { marginTop: 12, fontSize: 13, fontWeight: 500, marginBottom: 4 }, children: [ze('AnyDev 原始响应（透传，便于排错）', 'AnyDev Raw Response (passthrough for debugging)'), "\uFF1A"] }), _jsx("pre", { className: style.bridgeCmdBlock, style: { maxHeight: 240, overflow: 'auto' }, children: anydevResultData.rawResponseBody || '(empty)' }), _jsxs("details", { style: { marginTop: 8 }, children: [_jsx("summary", { style: { cursor: 'pointer', fontSize: 12, color: '#86909c' }, children: ze('查看下发给 AnyDev 的 startCmd', 'View startCmd submitted to AnyDev') }), _jsx("pre", { className: style.bridgeCmdBlock, style: { maxHeight: 160, overflow: 'auto', marginTop: 4 }, children: anydevResultData.startCmd })] })] })) })] }));
};
export default CrashClawWorkspacePanel;
